import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import RepinPriceButton from '@/components/RepinPriceButton';
import {
  ArrowLeft, Sparkles, Plus, X, TrendingUp, TrendingDown, Minus, Wand2,
  Repeat, Shield, Target, RotateCcw, CalendarDays,
  Zap, DollarSign, Check, AlertTriangle, Link2, History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine,
} from 'recharts';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { computeRisk, riskColorClasses, riskLabel } from '@/lib/riskScore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- Utility ---------------------------------------------------------------
const fmtMoney = (n, places = 2) => {
  if (n == null || !isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: places, maximumFractionDigits: places,
  });
};
const fmtPct = (n, places = 2) => (n == null || !isFinite(n) ? '—' : `${n.toFixed(places)}%`);

/**
 * useMatchMedia — minimal hook for responsive JS. Re-renders when the
 * viewport crosses the given media-query boundary. Used by the What
 * If? builder to choose between phone-style "cards in left column" and
 * desktop-style "empty-state hero row" layouts.
 *
 * Why a hook (vs Tailwind responsive classes)? We need to render the
 * three movable cards (DCA / Add-Remove / Custom Changes) in ONE
 * location at a time — duplicating the JSX would create test-id
 * collisions and double-mount controlled inputs. With a JS-driven
 * decision, exactly one instance lives in the DOM.
 */
function useMatchMedia(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    // addEventListener supported in all modern browsers; older Safari fallback.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);
  return matches;
}

// One row in the user's "scenario builder" list. Each represents a single
// change applied on top of the user's CURRENT portfolio.
//   action: 'add' | 'modify' | 'remove' | 'replace' | 'dca'
//   preset_key: optional tag so multi-select preset checkboxes can find &
//               toggle their corresponding change rows cleanly.
const makeBlankChange = () => ({
  id: Math.random().toString(36).slice(2, 9),
  action: 'add',
  ticker: '',
  shares: 0,
  cost: null,
  replaceWith: '',
  recoveryYears: 0,
  preset_key: null,
});

// ----- Projection engine -----------------------------------------------------
// Compounds each holding forward N years. priceGrowth + divGrowth are
// percent-per-year (negative supported for priceGrowth). DRIP runs for
// the FIRST `dripYears` years then dividends accumulate as cash for the
// remaining `years - dripYears`. Pass `dripYears = 0` for never-DRIP
// (the "Now" card) or `dripYears = years` for always-DRIP. Returns
// final {value, cash, income, shares-by-ticker}.
//
// Overrides (priceOverrides / divOverrides) can be either:
//   - a scalar number (permanent floor — used by permanent crash / cut)
//   - an object { crashed, original, recoverYears } (recovery schedule —
//     linear interpolation from `crashed` back to `original` over
//     `recoverYears` years, then normal growth resumes on top of
//     `original`).
function projectPortfolio({ positions, stockMap, years, priceGrowth, divGrowth, dripYears, priceOverrides = {}, divOverrides = {} }) {
  const pg = 1 + priceGrowth / 100;
  const dg = 1 + divGrowth / 100;
  const byTicker = new Map();
  positions.forEach((p) => {
    const sd = stockMap[p.ticker];
    if (!sd || !sd.price) return;
    const po = priceOverrides[p.ticker];
    const dov = divOverrides[p.ticker];
    const priceRec = (po && typeof po === 'object') ? po : null;
    const divRec = (dov && typeof dov === 'object') ? dov : null;
    const basePrice = priceRec ? priceRec.crashed : (po != null ? po : sd.price);
    const baseDiv = divRec ? divRec.crashed : (dov != null ? dov : (sd.dividend_rate || 0));
    const cur = byTicker.get(p.ticker) || {
      ticker: p.ticker,
      shares: 0,
      basePrice,
      baseDiv,
      priceRec,
      divRec,
      sd,
    };
    cur.shares += p.shares || 0;
    byTicker.set(p.ticker, cur);
  });

  // Effective price/div at END of year y (y=0 = today).
  const effPrice = (h, y) => {
    if (h.priceRec && h.priceRec.recoverYears > 0) {
      const { crashed, original, recoverYears } = h.priceRec;
      if (y <= recoverYears) {
        const frac = y / recoverYears;
        return crashed + (original - crashed) * frac;
      }
      return original * Math.pow(pg, y - recoverYears);
    }
    return h.basePrice * Math.pow(pg, y);
  };
  const effDiv = (h, y) => {
    if (h.divRec && h.divRec.recoverYears > 0) {
      const { crashed, original, recoverYears } = h.divRec;
      if (y <= recoverYears) {
        const frac = y / recoverYears;
        return crashed + (original - crashed) * frac;
      }
      return original * Math.pow(dg, y - recoverYears);
    }
    return h.baseDiv * Math.pow(dg, y);
  };

  let cash = 0;
  for (let y = 0; y < years; y++) {
    let yearDividends = 0;
    byTicker.forEach((h) => {
      const yearlyDiv = h.shares * effDiv(h, y);
      yearDividends += yearlyDiv;
      if (y < dripYears) {
        const reinvestPrice = effPrice(h, y + 1);
        if (reinvestPrice > 0) {
          h.shares += yearlyDiv / reinvestPrice;
        }
      }
    });
    if (y >= dripYears) cash += yearDividends;
  }

  // Final state at year N
  let totalValue = cash;
  let annualIncome = 0;
  const sectors = {};
  let riskSum = 0;
  let riskN = 0;
  const finalPositions = [];

  byTicker.forEach((h) => {
    const finalPrice = effPrice(h, years);
    const finalDiv = effDiv(h, years);
    const finalShares = h.shares;
    const value = finalPrice * finalShares;
    const inc = finalDiv * finalShares;
    totalValue += value;
    annualIncome += inc;
    const sec = h.sd.sector || 'Other';
    sectors[sec] = (sectors[sec] || 0) + value;
    const r = computeRisk(h.sd);
    if (r) { riskSum += r.score; riskN += 1; }
    finalPositions.push({
      ticker: h.ticker,
      shares: finalShares,
      finalPrice,
      value,
      income: inc,
      yieldPct: finalPrice > 0 ? (finalDiv / finalPrice) * 100 : 0,
      risk: r,
      name: h.sd.name,
      sector: h.sd.sector,
      baseYield: h.sd.dividend_yield,
    });
  });

  const yieldPct = totalValue > 0 ? (annualIncome / totalValue) * 100 : 0;
  const sectorRows = Object.entries(sectors)
    .map(([s, v]) => ({ sector: s, value: v, pct: totalValue > 0 ? (v / totalValue) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);

  return {
    value: Math.round(totalValue * 100) / 100,
    cash: Math.round(cash * 100) / 100,
    stocksValue: Math.round((totalValue - cash) * 100) / 100,
    income: Math.round(annualIncome * 100) / 100,
    monthly: Math.round((annualIncome / 12) * 100) / 100,
    yieldPct,
    avgRisk: riskN > 0 ? riskSum / riskN : null,
    sectorRows,
    positionCount: finalPositions.length,
    finalPositions,
  };
}

// Apply the user's `changes` list to original positions. Also accepts a
// special `dca` change which is rolled out as monthly buys over `years`.
// Returns simulated positions + cash injected from DCA + a textual summary.
function applyChanges(originalPositions, changes, stockMap, years, priceGrowth, divGrowth, drip) {
  const summary = [];
  const byTicker = new Map();
  const priceOverrides = {};
  const divOverrides = {};
  originalPositions.forEach((p) => {
    const cur = byTicker.get(p.ticker) || { ticker: p.ticker, shares: 0 };
    cur.shares += p.shares || 0;
    byTicker.set(p.ticker, cur);
  });

  // Two passes so crash/cut_div overrides apply correctly even if the
  // user listed them BEFORE the replace/add that introduces the target
  // ticker. Pass 1 = share-mutating operations + DCA. Pass 2 = price /
  // dividend overrides on the resulting portfolio.
  const sharePassActions = new Set(['add', 'modify', 'remove', 'replace', 'dca']);
  const overridePassActions = new Set(['crash', 'cut_div']);
  const orderedChanges = [
    ...changes.filter((c) => sharePassActions.has(c.action)),
    ...changes.filter((c) => overridePassActions.has(c.action)),
  ];

  let dcaCashFromDripDuringDca = 0; // accumulated dividends from DCA buys when DRIP is off

  orderedChanges.forEach((c) => {
    if (c.action === 'dca') {
      const tickers = (c.dca_tickers || []).filter(Boolean);
      const monthly = c.dca_monthly || 0;
      const split = c.dca_split === true;
      if (monthly <= 0 || tickers.length === 0) return;
      const totalMonths = years * 12;
      const monthlyPriceMul = Math.pow(1 + priceGrowth / 100, 1 / 12);
      const monthlyDivMul = Math.pow(1 + divGrowth / 100, 1 / 12);
      // Highest-yielder among selected (for split leftover allocation)
      const yielder = [...tickers].sort((a, b) => {
        const ay = (stockMap[a]?.dividend_yield) || 0;
        const by = (stockMap[b]?.dividend_yield) || 0;
        return by - ay;
      })[0];

      // Track NEW shares accumulated during DCA (separate from original
      // positions so we don't conflate cost basis), then merge at the end.
      const dcaShares = new Map(tickers.map((t) => [t, 0]));

      for (let m = 0; m < totalMonths; m++) {
        const priceMul = Math.pow(monthlyPriceMul, m);
        const divMul = Math.pow(monthlyDivMul, m);
        let allocations = {};
        if (split) {
          const per = monthly / tickers.length;
          tickers.forEach((t) => { allocations[t] = per; });
          // Leftover cents to highest yielder (cents-precision)
          const allocSum = tickers.reduce((s, t) => s + Math.floor(allocations[t] * 100) / 100, 0);
          const leftover = Math.round((monthly - allocSum) * 100) / 100;
          if (leftover !== 0 && yielder) allocations[yielder] = (allocations[yielder] || 0) + leftover;
        } else {
          // 'each' — buy $monthly worth of EACH ticker per month
          tickers.forEach((t) => { allocations[t] = monthly; });
        }
        Object.entries(allocations).forEach(([t, dollars]) => {
          const sd = stockMap[t];
          if (!sd?.price) return;
          const monthPrice = sd.price * priceMul;
          if (monthPrice <= 0) return;
          const newShares = dollars / monthPrice;
          dcaShares.set(t, (dcaShares.get(t) || 0) + newShares);
          // Dividends paid by these freshly-bought shares for the
          // remainder of the projection horizon.
          const monthsRemaining = totalMonths - m;
          // Average monthly div over remaining months ≈ baseDiv/12 * divMul (start) → baseDiv/12 * monthlyDivMul^totalMonths (end)
          // We'll account for these dividends in the projection step;
          // here we only track shares and let projectPortfolio handle income.
        });
      }

      // Merge DCA shares into byTicker so the projection picks them up.
      const totalSpent = (split ? monthly : monthly * tickers.length) * totalMonths;
      dcaShares.forEach((sh, t) => {
        if (sh <= 0) return;
        const cur = byTicker.get(t) || { ticker: t, shares: 0 };
        cur.shares += sh;
        byTicker.set(t, cur);
      });
      summary.push({
        kind: 'dca',
        text: split
          ? `DCA: ${fmtMoney(monthly)}/mo split across ${tickers.join(', ')} for ${years}y → ${fmtMoney(totalSpent, 0)}`
          : `DCA: ${fmtMoney(monthly)}/mo of EACH (${tickers.join(', ')}) for ${years}y → ${fmtMoney(totalSpent, 0)}`,
        tickers,
      });
      return;
    }

    const t = (c.ticker || '').toUpperCase().trim();
    if (!t) return;
    const sd = stockMap[t];
    const price = sd?.price || 0;
    if (c.action === 'add') {
      const exist = byTicker.get(t) || { ticker: t, shares: 0 };
      exist.shares += c.shares || 0;
      byTicker.set(t, exist);
      summary.push({
        kind: 'add',
        ticker: t,
        text_after: `${c.shares} sh @ ${fmtMoney(price)} = ${fmtMoney((c.shares || 0) * price)}`,
      });
    } else if (c.action === 'modify') {
      if (!byTicker.has(t)) return;
      const exist = byTicker.get(t);
      const old = exist.shares;
      exist.shares = c.shares || 0;
      if (exist.shares <= 0) byTicker.delete(t);
      summary.push({
        kind: 'modify',
        ticker: t,
        text_after: `set to ${exist.shares.toFixed(2)} sh (was ${old.toFixed(2)})`,
      });
    } else if (c.action === 'remove') {
      if (byTicker.has(t)) {
        const removed = byTicker.get(t);
        byTicker.delete(t);
        summary.push({
          kind: 'remove',
          ticker: t,
          text_after: `removed ${removed.shares.toFixed(2)} sh`,
        });
      }
    } else if (c.action === 'replace') {
      const r = (c.replaceWith || '').toUpperCase().trim();
      if (!byTicker.has(t) || !r) return;
      const old = byTicker.get(t);
      // Per spec: replacing a ticker preserves SHARE COUNT (not dollar
      // value). The user can always layer a `modify` change on top to
      // adjust the new share count.
      const oldShares = old.shares;
      const oldPrice = sd?.price || 0;
      const oldValue = oldShares * oldPrice;
      byTicker.delete(t);
      const targetSd = stockMap[r];
      const targetPrice = targetSd?.price || 0;
      const exist = byTicker.get(r) || { ticker: r, shares: 0 };
      exist.shares += oldShares;
      byTicker.set(r, exist);
      summary.push({
        kind: 'replace',
        ticker: t,
        ticker2: r,
        text_after: `${oldShares.toFixed(2)} sh of ${t} (${fmtMoney(oldValue)}) → ${oldShares.toFixed(2)} sh of`,
      });
    } else if (c.action === 'crash') {
      // Top Yielder Stock Crash — drops BOTH price AND dividend by the
      // crash percentage. A real-world crash usually triggers a div cut
      // alongside the price drop, so this preset models both. Users
      // who want price-only or dividend-only changes can use the
      // separate `cut_div` action.
      // If `recoveryYears > 0` the crash recovers linearly back to the
      // pre-crash value over that many years (realistic bounce-back).
      if (!byTicker.has(t)) return;
      const pct = Math.max(0, Math.min(100, Number(c.crashPct) || 75));
      const recoverYears = Math.max(0, Math.min(30, Number(c.recoveryYears) || 0));
      const startPrice = sd?.price || 0;
      const startDiv = sd?.dividend_rate || 0;
      const crashedPrice = startPrice * (1 - pct / 100);
      const crashedDiv = startDiv * (1 - pct / 100);
      if (recoverYears > 0) {
        priceOverrides[t] = { crashed: crashedPrice, original: startPrice, recoverYears };
        divOverrides[t] = { crashed: crashedDiv, original: startDiv, recoverYears };
      } else {
        priceOverrides[t] = crashedPrice;
        divOverrides[t] = crashedDiv;
      }
      summary.push({
        kind: 'crash',
        ticker: t,
        text_after: recoverYears > 0
          ? `crashes ${pct.toFixed(0)}% then recovers over ${recoverYears}y — price ${fmtMoney(startPrice)} → ${fmtMoney(crashedPrice)} → ${fmtMoney(startPrice)}`
          : `crashes ${pct.toFixed(0)}% — price ${fmtMoney(startPrice)} → ${fmtMoney(crashedPrice)}, dividend $${startDiv.toFixed(2)}/sh → $${crashedDiv.toFixed(2)}/sh`,
      });
    } else if (c.action === 'cut_div') {
      // Dividend Cut — overrides the ticker's base dividend rate.
      // Annual dividend growth is still applied to the cut rate, so a
      // cut today still slowly rebuilds over the horizon.
      // If `recoveryYears > 0` the dividend recovers linearly back to
      // the pre-cut rate over that many years.
      if (!byTicker.has(t)) return;
      const pct = Math.max(0, Math.min(100, Number(c.cutPct) || 50));
      const recoverYears = Math.max(0, Math.min(30, Number(c.recoveryYears) || 0));
      const startRate = sd?.dividend_rate || 0;
      const cutRate = startRate * (1 - pct / 100);
      if (recoverYears > 0) {
        divOverrides[t] = { crashed: cutRate, original: startRate, recoverYears };
      } else {
        divOverrides[t] = cutRate;
      }
      summary.push({
        kind: 'cut_div',
        ticker: t,
        text_after: recoverYears > 0
          ? `dividend cut ${pct.toFixed(0)}% then recovers over ${recoverYears}y ($${startRate.toFixed(2)}/sh → $${cutRate.toFixed(2)}/sh → $${startRate.toFixed(2)})`
          : `dividend cut ${pct.toFixed(0)}% ($${startRate.toFixed(2)}/sh → $${cutRate.toFixed(2)}/sh)`,
      });
    }
  });

  const simulated = Array.from(byTicker.values())
    .filter((x) => x.shares > 0)
    .map((x) => ({ ticker: x.ticker, shares: x.shares, cost_per_share: 0 }));
  return { simulated, summary, priceOverrides, divOverrides };
}

const ACTION_LABELS = {
  add: 'Add to portfolio',
  modify: 'Modify shares of',
  remove: 'Remove from portfolio',
  replace: 'Replace with another ticker',
  crash: 'Stock crash (price drops)',
  cut_div: 'Dividend cut',
};

const SECTOR_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#eab308',
];

// Benchmark-line colors for the Historical Backtest chart overlay. Chosen
// to be visually distinct from the portfolio-value (amber) and
// cumulative-dividends (emerald) lines already on that chart.
const BENCHMARK_COLORS = ['#3b82f6', '#a855f7'];

// ----- Inline ticker autocomplete -------------------------------------------
function TickerInput({ value, onChange, placeholder = 'Ticker', testId }) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [query, setQuery] = useState(value || '');
  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!query || query.length < 1) { setMatches([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      axios.get(`${API}/stocks/search`, { params: { q: query, limit: 6 } })
        .then((res) => { if (!cancelled) setMatches(res.data?.stocks || res.data || []); })
        .catch(() => setMatches([]));
    }, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value.toUpperCase()); onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder}
        className="font-mono text-sm h-9"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {matches.slice(0, 6).map((m) => (
            <li key={m.ticker}>
              <button
                type="button"
                onClick={() => { onChange(m.ticker); setQuery(m.ticker); setOpen(false); }}
                className="w-full text-left px-2 py-1 hover:bg-accent flex items-center justify-between gap-2"
                data-testid={`${testId}-option-${m.ticker}`}
              >
                <span className="font-mono text-xs font-bold">{m.ticker}</span>
                <span className="text-[10px] text-muted-foreground truncate">{m.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ----- Main page -------------------------------------------------------------

// Pure helper: given a raw per-ticker monthly series (from
// /api/whatif/backtest), initial share counts, DCA config, DRIP flag, and
// a target window in years, replay month-by-month and return the same
// result shape as the old `runBacktest` closure. Lives outside the
// component so the BacktestResultsPanel slider can recompute instantly on
// slide without refetching. Slicing is done from the TAIL of the master
// month list so the slider always shows the most recent N years.
function computeBacktestFromSeries(series, initialShares, dcaConfig, reinvestDrip, viewYears, benchmarkTickers = [], dripYears = null) {
  const allMonths = new Set();
  // Only user-held tickers contribute to the master month calendar —
  // benchmark series shouldn't drag the window wider than the user
  // portfolio actually covers.
  Object.entries(series || {}).forEach(([tkr, arr]) => {
    if (benchmarkTickers.includes(tkr)) return;
    (arr || []).forEach((m) => allMonths.add(m.date));
  });
  const fullList = Array.from(allMonths).sort();
  if (fullList.length < 2) return null;
  // +1 month for the starting snapshot anchor.
  const targetMonths = Math.max(2, Math.round(viewYears * 12) + 1);
  const monthList = fullList.length <= targetMonths
    ? fullList
    : fullList.slice(fullList.length - targetMonths);

  const byTickerDateMap = {};
  Object.entries(series || {}).forEach(([tkr, arr]) => {
    const m = {};
    (arr || []).forEach((row) => { m[row.date] = row; });
    byTickerDateMap[tkr] = m;
  });

  const shares = { ...initialShares };
  let cumulativeDividends = 0;
  let cumulativeContrib = 0;
  const points = [];

  const dcaEnabled = !!(dcaConfig && dcaConfig.enabled);
  const dcaMonthlyAmt = (dcaConfig && dcaConfig.monthly) || 0;
  const dcaTickers = (dcaConfig && dcaConfig.tickers) || [];
  const dcaSplit = !!(dcaConfig && dcaConfig.split);
  const dcaMonthly = (dcaEnabled && dcaMonthlyAmt > 0 && dcaTickers.length > 0) ? dcaMonthlyAmt : 0;

  // DRIP-cutoff support: when `reinvestDrip` is ON but `dripYears` is a
  // positive number less than the viewed window, DRIP reinvestment
  // stops after that many years (in months = dripYears * 12). Null /
  // undefined / 0 / >= viewYears means "full window DRIP" which is the
  // historical behaviour.
  const dripCutoffMonths = (reinvestDrip && dripYears != null && dripYears > 0 && dripYears < viewYears)
    ? Math.round(dripYears * 12)
    : null;

  monthList.forEach((date, idx) => {
    // idx counts from 0 = first month of the window. DRIP is active for
    // this month only when reinvestDrip is on AND (no cutoff OR
    // idx < cutoff).
    const dripActiveThisMonth = reinvestDrip && (dripCutoffMonths == null || idx < dripCutoffMonths);
    let valueThisMonth = 0;
    let divsThisMonth = 0;
    Object.keys(shares).forEach((tkr) => {
      const row = byTickerDateMap[tkr]?.[date];
      if (!row) return;
      const close = row.close || 0;
      const divPerShare = row.div || 0;
      const divAmt = shares[tkr] * divPerShare;
      divsThisMonth += divAmt;
      if (dripActiveThisMonth && divAmt > 0 && close > 0) {
        shares[tkr] += divAmt / close;
      }
      valueThisMonth += shares[tkr] * close;
    });
    if (dcaMonthly > 0 && idx > 0) {
      if (dcaSplit) {
        const per = dcaMonthly / dcaTickers.length;
        dcaTickers.forEach((tkr) => {
          const row = byTickerDateMap[tkr]?.[date];
          if (!row || !row.close) return;
          shares[tkr] = (shares[tkr] || 0) + per / row.close;
          cumulativeContrib += per;
        });
      } else {
        dcaTickers.forEach((tkr) => {
          const row = byTickerDateMap[tkr]?.[date];
          if (!row || !row.close) return;
          shares[tkr] = (shares[tkr] || 0) + dcaMonthly / row.close;
          cumulativeContrib += dcaMonthly;
        });
      }
      valueThisMonth = 0;
      Object.keys(shares).forEach((tkr) => {
        const close = byTickerDateMap[tkr]?.[date]?.close || 0;
        valueThisMonth += shares[tkr] * close;
      });
    }
    cumulativeDividends += divsThisMonth;
    points.push({
      date,
      value: Math.round(valueThisMonth * 100) / 100,
      cumDividends: Math.round(cumulativeDividends * 100) / 100,
      cumContrib: Math.round(cumulativeContrib * 100) / 100,
      monthIncome: Math.round(divsThisMonth * 100) / 100,
    });
  });

  const firstMonth = monthList[0];
  let startValue = 0;
  Object.keys(initialShares).forEach((tkr) => {
    const close = byTickerDateMap[tkr]?.[firstMonth]?.close || 0;
    startValue += initialShares[tkr] * close;
  });
  const endValue = points[points.length - 1]?.value || 0;

  // Benchmarks: for each benchmark ticker, simulate "put $startValue into
  // this benchmark on month 1 and hold (with DRIP matching user setting)"
  // so the chart shows whether the user's actual portfolio beat or
  // trailed SCHD/VYM over the same window. Skipped when startValue is 0
  // or the benchmark has no data in the window.
  const benchmarks = {};
  benchmarkTickers.forEach((btkr) => {
    const firstRow = byTickerDateMap[btkr]?.[firstMonth];
    if (!firstRow || !firstRow.close || startValue <= 0) return;
    let bShares = startValue / firstRow.close;
    const bPoints = [];
    monthList.forEach((date, idx) => {
      // Match user-portfolio DRIP cutoff so the benchmark line is a
      // fair apples-to-apples comparison.
      const dripActiveThisMonth = reinvestDrip && (dripCutoffMonths == null || idx < dripCutoffMonths);
      const row = byTickerDateMap[btkr]?.[date];
      if (!row || !row.close) {
        // Use last known value so the line is continuous.
        const prev = bPoints[bPoints.length - 1];
        bPoints.push({ date, value: prev ? prev.value : 0 });
        return;
      }
      const close = row.close;
      const divPerShare = row.div || 0;
      const divAmt = bShares * divPerShare;
      if (dripActiveThisMonth && divAmt > 0 && close > 0) {
        bShares += divAmt / close;
      }
      bPoints.push({ date, value: Math.round(bShares * close * 100) / 100 });
    });
    benchmarks[btkr] = bPoints;
  });

  // Merge benchmark values into the main points array so Recharts can
  // draw them as additional <Line> series on the same chart.
  const pointsWithBench = points.map((p, i) => {
    const out = { ...p };
    Object.keys(benchmarks).forEach((btkr) => {
      const bp = benchmarks[btkr][i];
      if (bp) out[`bench_${btkr}`] = bp.value;
    });
    return out;
  });

  return {
    startDate: firstMonth,
    endDate: monthList[monthList.length - 1],
    startValue: Math.round(startValue * 100) / 100,
    endValue,
    totalDividends: Math.round(cumulativeDividends * 100) / 100,
    totalContrib: Math.round(cumulativeContrib * 100) / 100,
    startShares: { ...initialShares },
    endShares: shares,
    points: pointsWithBench,
    benchmarks,
    benchmarkEndValues: Object.fromEntries(
      Object.entries(benchmarks).map(([btkr, bp]) => [btkr, bp[bp.length - 1]?.value || 0])
    ),
    monthsReplayed: points.length,
    // Exposed so the chart can draw a vertical reference line at the
    // month DRIP was cut off. Null when no cutoff applies (DRIP full
    // window, DRIP off entirely, or cutoff out of view).
    dripCutoffDate: dripCutoffMonths != null && dripCutoffMonths < monthList.length
      ? monthList[dripCutoffMonths]
      : null,
    dripCutoffMonths,
    // Opportunity cost of turning DRIP off at the cutoff: sum of
    // dividends received as CASH after that month (i.e. what would
    // otherwise have been reinvested). Only meaningful when
    // `dripCutoffMonths` is set and inside the window. Additionally,
    // compound each post-cutoff cash dividend forward to the end of
    // the window at the portfolio's measured price-appreciation rate
    // over the backtest — this gives the "what it could have grown
    // into" theoretical value. Uses a simple monthly growth rate
    // derived from the portfolio's own end/start value.
    dripOpportunity: (() => {
      if (dripCutoffMonths == null || dripCutoffMonths >= points.length) return null;
      const cashDivs = points
        .slice(dripCutoffMonths)
        .reduce((acc, p) => acc + (p.monthIncome || 0), 0);
      // Monthly growth rate implied by portfolio end/start over the
      // total replay window. Guard against zero / negative starts.
      const totalMonths = points.length - 1;
      let monthlyRate = 0;
      if (startValue > 0 && endValue > 0 && totalMonths > 0) {
        monthlyRate = Math.pow(endValue / startValue, 1 / totalMonths) - 1;
      }
      // Future value of each cash dividend compounded to the end of
      // the window at monthlyRate. Adds realistic "what it could
      // have grown to" rather than nominal sum.
      let couldHaveGrownTo = 0;
      for (let i = dripCutoffMonths; i < points.length; i += 1) {
        const d = points[i].monthIncome || 0;
        const monthsToEnd = (points.length - 1) - i;
        couldHaveGrownTo += d * Math.pow(1 + monthlyRate, monthsToEnd);
      }
      return {
        cashReceived: Math.round(cashDivs * 100) / 100,
        couldHaveGrownTo: Math.round(couldHaveGrownTo * 100) / 100,
        // Extra upside = the compounded value minus nominal.
        foregoneGrowth: Math.round((couldHaveGrownTo - cashDivs) * 100) / 100,
      };
    })(),
  };
}

export default function WhatIf() {
  // Phase C (May 2026) — prices are pinned at page-open time. The
  // `stockMap` is hydrated once in `useEffect` and every projection /
  // simulation reads from that snapshot. WebSocket price updates that
  // stream into stock_cache do NOT mutate scenarios already running on
  // this page. The inline "Refresh price" link below the page header
  // re-pulls /stocks/batch and merges the freshest prices in-place,
  // preserving every staged Change and preset toggle.
  const [positions, setPositions] = useState([]);
  // Timestamp of the last successful price hydration. Set on bootstrap
  // and on every "Refresh price" click. Drives the inline tooltip.
  const [pinnedAt, setPinnedAt] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState([]);
  const [years, setYears] = useState(5);
  const [growthRate, setGrowthRate] = useState(6);
  const [priceUpside, setPriceUpside] = useState(3);
  const [reinvestDrip, setReinvestDrip] = useState(true);
  // dripYears: how many of the time-machine years use DRIP. Defaults to
  // `years` (full DRIP) but the "DRIP for 5 years" preset overrides this
  // independently so users can model "DRIP first 5y → cash next 5y".
  // Null means "follow `years`" (full DRIP).
  const [dripYears, setDripYears] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);

  // Active preset checkboxes — keyed by preset name.
  const [activePresets, setActivePresets] = useState({});

  // Recovery period applied to ALL crash/cut preset-generated changes.
  // 0 = permanent (default); N > 0 = linear recovery over N years.
  const [presetRecoveryYears, setPresetRecoveryYearsState] = useState(0);

  // Reverse Mode — historical backtest. When ON, the "What If?" button
  // replays real monthly Yahoo price + dividend history over the last
  // `years` years instead of projecting forward. Validates DCA/DRIP
  // discipline against actual market behaviour.
  const [reverseMode, setReverseMode] = useState(false);
  const [backtestResult, setBacktestResult] = useState(null);

  // Empty-state hero layout (desktop only — see useMatchMedia).
  // When BOTH (a) no simulation/backtest result exists AND (b) viewport
  // is md+ (≥768px), the three movable cards (DCA, Add/Remove, Custom
  // Changes) render side-by-side under the empty-state message instead
  // of stacked in the left column. The moment a simulation is run
  // (or the user is on a phone), they snap back into the left column.
  // Only ONE instance of each card is mounted at any given time —
  // chosen via JS to avoid test-id collisions and double-mounted state.
  const isMdUp = useMatchMedia('(min-width: 768px)');

  // Tracks the config "snapshot key" at the moment the user last clicked
  // What If? (or Run Backtest). If the current config still matches this
  // snapshot, the results panel title reads "Changes Applied". As soon
  // as the user edits anything (slider, adds a change, toggles DRIP,
  // etc.) the live key drifts and the title flips to "Changes to be
  // Applied" — a clear nudge that they need to re-run.
  const [lastAppliedKey, setLastAppliedKey] = useState(null);
  const currentConfigKeyRef = useRef(null);

  // DCA preset state — `enabled` defaults OFF (user spec); when false the
  // DCA change is excluded from the simulation regardless of tickers/amount.
  const [dca, setDca] = useState({ enabled: false, monthly: 200, tickers: [], split: false });

  // Input drafts for the Add/Remove box and Custom Changes box. These are
  // the FORM STATE for a single pending entry — the user fills them in and
  // clicks Apply to commit the entry into `changes`. This replaces the
  // older "grow-a-new-row per click" pattern.
  const [addRemoveMode, setAddRemoveMode] = useState('add'); // 'add' | 'remove'
  const [addRemoveDraft, setAddRemoveDraft] = useState({ ticker: '', shares: 0 });

  // Tickers currently being force-refreshed via /api/stocks/{T}?force=true.
  // Rendered as a small "fetching fresh data…" hint so the user knows
  // the simulator is gathering up-to-date prices + dividends BEFORE they
  // click What If?. If the fresh fetch fails (Yahoo + Massive both
  // down), we keep the spinner up for a minimum of 5 seconds then fall
  // back to whatever stale cache we have — so the UX is never abrupt.
  const [prefetchingTickers, setPrefetchingTickers] = useState(new Set());

  // Client-side per-ticker rate limit: never force-refresh the same
  // ticker more than once per 5 minutes (Yahoo throttles aggressive
  // re-fetches). Stored in a ref so re-renders don't reset the map.
  const lastPrefetchAtRef = useRef({});

  // Dividend Kings pool — fetched once on mount so the swap-riskiest-to-
  // King preset can pick a RANDOM King instead of always defaulting to
  // KO. Null = still loading; [] = load failed (we'll fall back to KO).
  const [kingsPool, setKingsPool] = useState(null);

  // Ref to the right-column results wrapper. On phone/tablet viewport
  // (<1024px, below the `lg:` breakpoint where the grid switches to
  // 3 columns), the Changes Applied + Now + What-If cards render BELOW
  // the entire scenario builder. So a user tapping the sticky top
  // "What If?" button sees no visible change — they have to scroll
  // halfway down the page to notice anything happened. `scrollToResults`
  // smooth-scrolls this ref into view on narrow viewports only.
  const resultsRef = useRef(null);
  const scrollToResults = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return; // desktop: already visible
    const el = resultsRef.current;
    if (!el) return;
    // Offset by the sticky header (~56px) + the sticky top run bar
    // (~56px) so the Changes Applied title isn't hidden underneath.
    const STICKY_OFFSET = 120;
    const y = el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }, []);

  // Brief visual "got it!" feedback on the What If? / Backtest button so
  // users know something happened even before the results appear. Set
  // for 1.4s right after the sim resolves; combined with a keyframe
  // animation (see `@keyframes whatif-pulse` in the CSS tail below)
  // this makes the button scale, glow, and flash a sparkle ring.
  const [justRan, setJustRan] = useState(false);
  const pulseTimerRef = useRef(null);
  const flashJustRan = useCallback(() => {
    setJustRan(true);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setJustRan(false), 1400);
  }, []);
  useEffect(() => () => { if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current); }, []);
  const [customDraft, setCustomDraft] = useState({
    action: 'modify', ticker: '', replaceWith: '', shares: 0, crashPct: 75, cutPct: 50, recoveryYears: 0,
  });
  // Changes Applied expand/collapse on the right panel.
  const [showAllChanges, setShowAllChanges] = useState(false);

  // Glossary click guard — when a user clicks ANY link or button whose
  // href/onclick leads to the Glossary route, we intercept and show a
  // confirmation modal so they don't lose their work. The modal offers
  // four exits: Continue (ditch state), Save & Continue (save as
  // "_autosave" then navigate), Open in new tab, Cancel.
  const [glossaryModal, setGlossaryModal] = useState({ open: false, target: '/glossary' });

  useEffect(() => {
    // Only enable guard when user has any uncommitted state on the page.
    const hasWork = () => changes.length > 0 || Object.values(activePresets).some(Boolean) || dca.enabled || simResult != null || backtestResult != null || reverseMode;
    // Append `?from=whatif` to every glossary nav so the Glossary page
    // can render a "← Back to What If?" breadcrumb.
    const withFrom = (href) => {
      if (!href) return '/glossary?from=whatif';
      const sep = href.includes('?') ? '&' : '?';
      return href.includes('from=') ? href : `${href}${sep}from=whatif`;
    };
    const handler = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('/glossary')) return;
      if (!hasWork()) {
        // No work to lose — let the nav happen but still tag the URL
        // so the breadcrumb appears on the Glossary page.
        e.preventDefault();
        e.stopPropagation();
        window.location.href = withFrom(href);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setGlossaryModal({ open: true, target: withFrom(href) });
    };
    document.addEventListener('click', handler, true); // capture phase
    return () => document.removeEventListener('click', handler, true);
  }, [changes, activePresets, dca, simResult, backtestResult, reverseMode]);

  // Saved scenarios (server-stored, cross-device)
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');

  const refreshSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/whatif-saves`, { withCredentials: true });
      setSavedScenarios(r.data?.saves || []);
    } catch { setSavedScenarios([]); }
  }, []);
  useEffect(() => { refreshSaved(); }, [refreshSaved]);

  // Fetch Dividend Kings pool once on mount so the "Swap riskiest → King"
  // preset can pick a RANDOM King each time (instead of always KO).
  // Non-blocking: if the fetch fails, the preset silently falls back to KO.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/stocks/kings-challenge`, {
          withCredentials: true,
          timeout: 10000,
        });
        if (cancelled) return;
        const list = (r.data?.stocks || [])
          .filter((s) => s?.ticker && (s.price || 0) > 0)
          .map((s) => ({ ticker: s.ticker, name: s.name, yield: s.dividend_yield || 0 }));
        setKingsPool(list);
      } catch {
        if (!cancelled) setKingsPool([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveScenario = useCallback(async () => {
    const name = (saveName || '').trim();
    if (!name) { toast.error('Give the scenario a name'); return; }
    // When the user has a live backtest on-screen, force reverseMode=true
    // in the saved payload so loading the scenario back auto-flips the
    // Reverse Mode checkbox and the run button turns amber "Run Backtest".
    // Covers the case where the user may have momentarily unticked the
    // box before clicking Save Current, and also back-fills the flag for
    // pre-round-19 scenarios where it was never persisted.
    const effectiveReverseMode = reverseMode || backtestResult != null;
    try {
      await axios.post(`${API}/whatif-saves`, {
        name,
        scenario: {
          changes, years, growthRate, priceUpside, reinvestDrip, dripYears,
          activePresets, dca, presetRecoveryYears, reverseMode: effectiveReverseMode,
        },
      }, { withCredentials: true });
      setShowSaveDialog(false);
      setSaveName('');
      toast.success(`Saved "${name}"`, {
        description: effectiveReverseMode
          ? 'Loads with Reverse Mode on — just click Run Backtest to replay.'
          : 'Loads with forward-projection settings ready to go.',
      });
      refreshSaved();
    } catch (e) {
      toast.error('Could not save', { description: e?.response?.data?.detail || e?.message });
    }
  }, [saveName, changes, years, growthRate, priceUpside, reinvestDrip, dripYears, activePresets, dca, presetRecoveryYears, reverseMode, backtestResult, refreshSaved]);

  const handleLoadScenario = (saved) => {
    const s = saved.scenario || {};
    setChanges(s.changes || []);
    setYears(s.years ?? 5);
    setGrowthRate(s.growthRate ?? 6);
    setPriceUpside(s.priceUpside ?? 3);
    setReinvestDrip(s.reinvestDrip ?? true);
    setDripYears(s.dripYears ?? null);
    setActivePresets(s.activePresets || {});
    setDca(s.dca || { enabled: false, monthly: 200, tickers: [], split: false });
    setPresetRecoveryYearsState(s.presetRecoveryYears ?? 0);
    setReverseMode(!!s.reverseMode);
    setSimResult(null);
    setBacktestResult(null);
    toast.success(`Loaded "${saved.name}"`, { description: s.reverseMode ? 'Click Run Backtest to replay.' : 'Click What If? to run.' });
  };

  const handleDeleteScenario = async (name) => {
    try {
      // Use the body-based delete endpoint so scenario names that
      // contain forward slashes (legacy autosave timestamps like
      // "_autosave 5/2/2026, 3:45:12 PM") can still be deleted — the
      // path-based endpoint breaks on those because proxies decode
      // %2F back to / before FastAPI routes the request.
      await axios.post(`${API}/whatif-saves/delete`, { name }, { withCredentials: true });
      refreshSaved();
      toast.success(`Deleted "${name}"`);
    } catch (e) {
      toast.error('Could not delete', {
        description: e?.response?.data?.detail || e?.message || 'Try again.',
      });
    }
  };

  // Share Scenario — copies a shareable URL to the clipboard. Scenario
  // params are JSON-then-base64-encoded into `?s=…`. Anyone who opens
  // the link lands on /whatif with the scenario pre-loaded (no login
  // required to view; they just can't save it to their own account
  // until they sign in).
  const handleShareScenario = async (saved) => {
    // Generate the URL first — only the clipboard write can fail, and
    // when it does the user should still get the URL so they can copy
    // it manually (common cause: browsers blocking clipboard writes in
    // non-secure contexts, iframes, or without a fresh user gesture).
    let url = '';
    try {
      const payload = JSON.stringify({
        name: saved.name,
        scenario: saved.scenario || {},
      });
      // btoa doesn't handle unicode — encode to URI first.
      const b64 = btoa(unescape(encodeURIComponent(payload)));
      url = `${window.location.origin}/whatif?s=${encodeURIComponent(b64)}`;
    } catch (e) {
      toast.error('Could not generate share link', { description: e?.message || 'Unexpected encoding error.' });
      return;
    }

    // Try clipboard APIs with graceful fallbacks.
    const copyViaExec = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch { return false; }
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied!', {
          description: 'Paste anywhere — opens /whatif with the scenario pre-loaded.',
        });
        return;
      }
      if (copyViaExec()) {
        toast.success('Share link copied!', {
          description: 'Paste anywhere — opens /whatif with the scenario pre-loaded.',
        });
        return;
      }
      throw new Error('clipboard unavailable');
    } catch (e) {
      // Clipboard blocked — render a custom toast with a one-tap Copy
      // button (so phone users don't have to long-press-select the raw
      // URL) and an explicit Close button so they can dismiss it any
      // time. Toast stays open until the user closes it.
      // eslint-disable-next-line no-console
      console.log('Shareable What-If URL:', url);
      toast.custom((t) => (
        <div
          data-testid="whatif-share-fallback-toast"
          className="relative rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/80 dark:border-amber-700/70 text-amber-950 dark:text-amber-100 shadow-xl px-3 py-3 min-w-[280px] max-w-sm"
        >
          {/* Dismiss X (top-left as requested) */}
          <button
            type="button"
            onClick={() => toast.dismiss(t)}
            className="absolute top-1.5 left-1.5 p-0.5 rounded hover:bg-amber-200/60 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-200"
            aria-label="Dismiss"
            data-testid="whatif-share-fallback-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="pl-6 pr-1 text-[11px] font-bold uppercase tracking-wider mb-1.5">
            Copy this link manually
          </div>
          <div className="text-[10px] break-all font-mono bg-amber-100/60 dark:bg-amber-900/40 rounded px-2 py-1.5 mb-2 select-all max-h-24 overflow-auto pb-24 sm:pb-4">
            {url}
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(url);
                } else {
                  copyViaExec();
                }
                toast.success('Share link copied!');
                toast.dismiss(t);
              } catch {
                toast.error('Still blocked — long-press the URL above and tap Copy.');
              }
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold py-1.5"
            data-testid="whatif-share-fallback-copy"
          >
            <Link2 className="w-3.5 h-3.5" /> Copy Link
          </button>
        </div>
      ), { duration: Infinity });
    }
  };

  // URL-param loader — if someone opened /whatif?s=<b64>, decode and
  // apply the scenario. Runs once on mount.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const s = u.searchParams.get('s');
      if (!s) return;
      const decoded = decodeURIComponent(escape(atob(decodeURIComponent(s))));
      const obj = JSON.parse(decoded);
      const scen = obj?.scenario;
      if (!scen) return;
      setChanges(scen.changes || []);
      setYears(scen.years ?? 5);
      setGrowthRate(scen.growthRate ?? 6);
      setPriceUpside(scen.priceUpside ?? 3);
      setReinvestDrip(scen.reinvestDrip ?? true);
      setDripYears(scen.dripYears ?? null);
      setActivePresets(scen.activePresets || {});
      setDca(scen.dca || { enabled: false, monthly: 200, tickers: [], split: false });
      setPresetRecoveryYearsState(scen.presetRecoveryYears ?? 0);
      setReverseMode(!!scen.reverseMode);
      // Strip ?s= so a refresh doesn't re-apply.
      u.searchParams.delete('s');
      window.history.replaceState({}, '', u.pathname + (u.search ? u.search : ''));
      toast.success(`Loaded shared scenario "${obj.name || 'shared'}"`, {
        description: 'Adjust it then click What If? to run.',
      });
    } catch { /* ignore malformed share links */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bootstrap
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const posRes = await axios.get(`${API}/portfolio/owned-tickers`, { withCredentials: true });
        const tickers = (posRes.data || []).map((d) => d.ticker || d).filter(Boolean);
        if (cancelled) return;
        if (tickers.length === 0) { setLoading(false); return; }
        const posDetail = await Promise.all(
          tickers.map((t) =>
            axios.get(`${API}/portfolio/${t}/positions`, { withCredentials: true })
              .then((r) => r.data).catch(() => [])
          )
        );
        const flat = posDetail.flat();
        const map = {};
        for (let i = 0; i < tickers.length; i += 20) {
          const chunk = tickers.slice(i, i + 20);
          try {
            const res = await axios.get(`${API}/stocks/batch/${chunk.join(',')}`, { withCredentials: true });
            (res.data || []).forEach((s) => { if (s?.ticker) map[s.ticker] = s; });
          } catch {}
        }
        if (cancelled) return;
        setPositions(flat.map((p) => ({ ticker: p.ticker, shares: p.shares, cost_per_share: p.cost_per_share })));
        setStockMap(map);
        setPinnedAt(new Date().toISOString());
      } catch (e) {
        toast.error('Could not load your portfolio', { description: 'Make sure you are signed in.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase C (May 2026) refresh: re-pull /stocks/batch for every ticker
  // currently in stockMap and merge fresh prices in-place. Does NOT
  // touch `positions`, `changes`, `presets`, or any other user state,
  // so an active simulation stays exactly as the user staged it — only
  // the underlying prices get re-pinned to the freshest WS values.
  const handleRefreshPrices = async () => {
    if (refreshingPrices) return;
    const tickers = Object.keys(stockMap);
    if (tickers.length === 0) return;
    setRefreshingPrices(true);
    try {
      const fresh = {};
      for (let i = 0; i < tickers.length; i += 20) {
        const chunk = tickers.slice(i, i + 20);
        try {
          const res = await axios.get(`${API}/stocks/batch/${chunk.join(',')}`, { withCredentials: true });
          (res.data || []).forEach((s) => { if (s?.ticker) fresh[s.ticker] = s; });
        } catch { /* tolerate per-chunk failure */ }
      }
      setStockMap((prev) => {
        const next = { ...prev };
        Object.entries(fresh).forEach(([t, s]) => {
          next[t] = { ...(prev[t] || {}), ...s };
        });
        return next;
      });
      setPinnedAt(new Date().toISOString());
    } finally {
      setRefreshingPrices(false);
    }
  };

  // ranked candidate buckets (used by presets)
  const ranked = useMemo(() => {
    const items = positions.map((p) => ({ ticker: p.ticker, shares: p.shares, sd: stockMap[p.ticker] || {} }))
      .filter((x) => x.sd?.price);
    return {
      byYield: [...items].sort((a, b) => (b.sd.dividend_yield || 0) - (a.sd.dividend_yield || 0)),
      byPerf: [...items].sort((a, b) => (b.sd.perf_1y || -Infinity) - (a.sd.perf_1y || -Infinity)),
      byRisk: [...items].sort((a, b) => (computeRisk(b.sd)?.score || 5) - (computeRisk(a.sd)?.score || 5)),
    };
  }, [positions, stockMap]);

  // Effective ticker list = original portfolio minus any 'remove' or
  // 'replace' targets, plus any 'add' or 'replace' replacements. Used
  // by the dropdowns inside Modify / Replace / Crash / Cut rows so
  // users always see the simulated portfolio's tickers (e.g. KO appears
  // after a swap-risky preset adds it).
  const effectiveTickers = useMemo(() => {
    const set = new Set(positions.map((p) => p.ticker));
    changes.forEach((c) => {
      const t = (c.ticker || '').toUpperCase();
      if (c.action === 'add' && t) set.add(t);
      else if (c.action === 'remove' && t) set.delete(t);
      else if (c.action === 'replace' && t && c.replaceWith) {
        set.delete(t);
        set.add(c.replaceWith.toUpperCase());
      }
    });
    return Array.from(set).filter(Boolean).sort();
  }, [positions, changes]);

  // Two sub-lists — drives the split UI (Add/Remove box vs Custom Changes box).
  const addRemoveChanges = changes.filter((c) => c.action === 'add' || c.action === 'remove');
  const customChanges = changes.filter((c) => c.action === 'modify' || c.action === 'replace' || c.action === 'crash' || c.action === 'cut_div');

  // Build a change for a given preset key (or null if portfolio empty).
  // For presets that depend on the post-swap ticker list (crash, cut-div),
  // we compute the effective ticker list AFTER any active replace preset
  // so the auto-pick reflects the King substitution.
  // Force-refresh one or more tickers via /api/stocks/{T}?force=true so
  // the sim uses live data when the user clicks What If?. Fire-and-
  // forget from the caller's perspective (resolves in the background).
  // 5-minute per-ticker cooldown keeps Yahoo happy. On fresh-fetch
  // failure, waits a minimum of 5 seconds before falling back to
  // ?stale_ok=true so the user has clear visual feedback.
  // DECLARED EARLY so the buildPresetChange useCallback below can
  // reference it without a temporal-dead-zone error.
  const prefetchFresh = useCallback(async (tickers) => {
    const list = (Array.isArray(tickers) ? tickers : [tickers])
      .map((t) => (t || '').toUpperCase().trim())
      .filter(Boolean);
    if (list.length === 0) return;
    const now = Date.now();
    const eligible = list.filter((t) => {
      const last = lastPrefetchAtRef.current[t] || 0;
      return (now - last) > 5 * 60 * 1000;
    });
    if (eligible.length === 0) return;
    eligible.forEach((t) => { lastPrefetchAtRef.current[t] = now; });
    setPrefetchingTickers((prev) => {
      const next = new Set(prev);
      eligible.forEach((t) => next.add(t));
      return next;
    });
    const unmark = (t) => setPrefetchingTickers((prev) => {
      if (!prev.has(t)) return prev;
      const next = new Set(prev);
      next.delete(t);
      return next;
    });
    await Promise.all(eligible.map(async (t) => {
      const startedAt = Date.now();
      try {
        const r = await axios.get(`${API}/stocks/${t}`, {
          params: { force: true },
          timeout: 12000,
        });
        if (r.data && r.data.ticker) {
          setStockMap((prev) => ({ ...prev, [r.data.ticker]: r.data }));
        }
      } catch {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, 5000 - elapsed);
        if (remaining > 0) await new Promise((res) => setTimeout(res, remaining));
        try {
          const stale = await axios.get(`${API}/stocks/${t}`, {
            params: { stale_ok: true },
            timeout: 5000,
          });
          if (stale.data && stale.data.ticker) {
            setStockMap((prev) => ({ ...prev, [stale.data.ticker]: stale.data }));
            toast.info(`Using cached data for ${t}`, {
              description: 'Massive failed — projection uses the latest cached price and dividend.',
            });
          }
        } catch {
          toast.error(`No data available for ${t}`, {
            description: 'Fresh fetch failed and nothing is cached. Try again in a minute.',
          });
        }
      } finally {
        unmark(t);
      }
    }));
  }, []);

  const buildPresetChange = useCallback(async (key) => {
    // Effective ticker list = original portfolio tickers minus any that
    // are being replaced via an active swap-risky preset, plus the
    // replacement target. This is the same logic the user described:
    // "if one of the tickers is swapped with a king then don't show
    // that one just the new swapped ticker (king)."
    const replacedSet = new Set();
    const replacementTickers = new Set();
    changes.forEach((c) => {
      if (c.action === 'replace' && c.ticker && c.replaceWith) {
        replacedSet.add(c.ticker.toUpperCase());
        replacementTickers.add(c.replaceWith.toUpperCase());
      }
    });
    const effectiveTickers = [
      ...positions.map((p) => p.ticker).filter((t) => !replacedSet.has(t)),
      ...replacementTickers,
    ].filter((t, i, a) => a.indexOf(t) === i);

    if (key === 'boost-yield') {
      const top = ranked.byYield[0];
      if (!top) return null;
      return {
        ...makeBlankChange(),
        action: 'add',
        ticker: top.ticker,
        shares: Math.max(1, Math.round(5000 / (top.sd.price || 1))),
        preset_key: key,
      };
    }
    if (key === 'replace-risky') {
      const riskiest = ranked.byRisk[0];
      if (!riskiest) return null;
      // Pool of Dividend Kings fetched on mount. Exclude any King the
      // user already owns so the preset actually diversifies, then pick
      // one at random. If the pool didn't load (or is exhausted), fall
      // back to the first owned King, or finally to KO as a safe default.
      const ownedTickers = new Set(positions.map((p) => p.ticker));
      let king = null;
      if (Array.isArray(kingsPool) && kingsPool.length > 0) {
        const candidates = kingsPool.filter(
          (k) => !ownedTickers.has(k.ticker) && k.ticker !== riskiest.ticker
        );
        if (candidates.length > 0) {
          king = candidates[Math.floor(Math.random() * candidates.length)].ticker;
        }
      }
      if (!king) {
        king = positions.find((p) => stockMap[p.ticker]?.dividend_tier === 'king')?.ticker;
      }
      if (!king) {
        king = 'KO';
      }
      if (!stockMap[king]) {
        try {
          const r = await axios.get(`${API}/stocks/${king}`);
          if (r.data) setStockMap((prev) => ({ ...prev, [king]: r.data }));
        } catch {}
      }
      // Kick off a fresh force-refresh for the King so the simulator
      // shows up-to-date price + dividend when the user clicks What If?.
      prefetchFresh(king);
      return { ...makeBlankChange(), action: 'replace', ticker: riskiest.ticker, replaceWith: king, preset_key: key };
    }
    if (key === 'double-best') {
      const top = ranked.byPerf[0];
      if (!top) return null;
      return { ...makeBlankChange(), action: 'modify', ticker: top.ticker, shares: top.shares * 2, preset_key: key };
    }
    if (key === 'cut-top-yielder') {
      // Pick the highest-yielding ticker in the EFFECTIVE list (post-swap).
      const candidates = effectiveTickers
        .map((t) => ({ ticker: t, y: stockMap[t]?.dividend_yield || 0 }))
        .filter((x) => x.y > 0)
        .sort((a, b) => b.y - a.y);
      if (candidates.length === 0) return null;
      return {
        ...makeBlankChange(),
        action: 'cut_div',
        ticker: candidates[0].ticker,
        cutPct: 50,
        recoveryYears: presetRecoveryYears,
        preset_key: key,
      };
    }
    if (key === 'crash') {
      // Top Yielder Stock Crash — auto-picks the highest-yielding ticker
      // in the EFFECTIVE list (post any active swap-king replacement) so
      // the crash hits the position the user is most worried about.
      const candidates = effectiveTickers
        .map((t) => ({ ticker: t, y: stockMap[t]?.dividend_yield || 0 }))
        .filter((x) => x.y > 0)
        .sort((a, b) => b.y - a.y);
      if (candidates.length === 0) return null;
      return {
        ...makeBlankChange(),
        action: 'crash',
        ticker: candidates[0].ticker,
        crashPct: 75,
        recoveryYears: presetRecoveryYears,
        preset_key: key,
      };
    }
    return null;
  }, [ranked, positions, stockMap, changes, presetRecoveryYears, kingsPool, prefetchFresh]);

  // Update the recovery period on any currently-active crash / cut-top-yielder
  // preset rows, AND remember it for future preset activations in this session.
  const setPresetRecovery = useCallback((yrs) => {
    const v = Math.max(0, Math.min(30, Number(yrs) || 0));
    setPresetRecoveryYearsState(v);
    setChanges((prev) => prev.map((c) => {
      if (c.preset_key === 'crash' || c.preset_key === 'cut-top-yielder') {
        return { ...c, recoveryYears: v };
      }
      return c;
    }));
  }, []);

  // Toggle a preset checkbox — adds or removes its corresponding change row.
  const togglePreset = useCallback(async (key) => {
    if (key === 'drip-5y') {
      // "DRIP for 5 years" — locks dripYears=5 INDEPENDENTLY of the
      // time-machine years setting. So if user has time machine = 10y,
      // DRIP runs years 0-5 and dividends accumulate as cash years 5-10.
      // Unchecking restores dripYears to null (follow `years`, full DRIP).
      const willActivate = !activePresets[key];
      if (willActivate) {
        setReinvestDrip(true);
        setDripYears(5);
      } else {
        setDripYears(null);
      }
      setActivePresets((p) => ({ ...p, [key]: willActivate }));
      return;
    }

    const isOn = !!activePresets[key];
    if (isOn) {
      setChanges((prev) => prev.filter((c) => c.preset_key !== key));
      setActivePresets((p) => ({ ...p, [key]: false }));
      return;
    }

    const change = await buildPresetChange(key);
    if (!change) {
      toast.info('Need at least one stock in your portfolio for this scenario.');
      return;
    }
    setChanges((prev) => [...prev, change]);
    setActivePresets((p) => ({ ...p, [key]: true }));
    toast.success(`Loaded "${PRESET_LABELS[key]}"`, { description: 'Click What If? to see the impact.' });
  }, [activePresets, buildPresetChange]);

  const addBlankChange = () => setChanges((c) => [...c, makeBlankChange()]);
  const updateChange = (id, patch) => setChanges((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  // Force-refresh one or more tickers via /api/stocks/{T}?force=true so
  // the sim uses live data when the user clicks What If?. Fire-and-
  // forget from the caller's perspective (resolves in the background).
  // On fresh-fetch failure, waits a minimum of 5 seconds before falling
  // back to ?stale_ok=true so the user has clear visual feedback that a
  // refresh was attempted.
  // Commit the Add/Remove draft into the changes list and reset the form.
  const applyAddRemove = () => {
    const t = (addRemoveDraft.ticker || '').toUpperCase().trim();
    if (!t) { toast.error('Pick a ticker first'); return; }
    if (addRemoveMode === 'add') {
      if (!addRemoveDraft.shares || addRemoveDraft.shares <= 0) {
        toast.error('Enter a share count greater than 0'); return;
      }
      setChanges((c) => [...c, {
        ...makeBlankChange(), action: 'add', ticker: t, shares: addRemoveDraft.shares,
      }]);
      // Background-refresh the newly-added ticker so What If? has live
      // data instead of waiting to discover the cache is stale.
      prefetchFresh(t);
    } else {
      setChanges((c) => [...c, { ...makeBlankChange(), action: 'remove', ticker: t }]);
    }
    setAddRemoveDraft({ ticker: '', shares: 0 });
  };

  // Commit the Custom Changes draft.
  const applyCustomChange = () => {
    const t = (customDraft.ticker || '').toUpperCase().trim();
    if (!t) { toast.error('Pick a ticker first'); return; }
    const base = { ...makeBlankChange(), ticker: t };
    if (customDraft.action === 'modify') {
      if (!customDraft.shares || customDraft.shares < 0) {
        toast.error('Enter a new share count'); return;
      }
      setChanges((c) => [...c, { ...base, action: 'modify', shares: customDraft.shares }]);
    } else if (customDraft.action === 'replace') {
      const r = (customDraft.replaceWith || '').toUpperCase().trim();
      if (!r) { toast.error('Pick a target ticker to replace with'); return; }
      setChanges((c) => [...c, { ...base, action: 'replace', replaceWith: r }]);
      // Background-refresh the replacement ticker so its live price/
      // dividend feed is ready before the user clicks What If?.
      prefetchFresh(r);
    } else if (customDraft.action === 'crash') {
      setChanges((c) => [...c, { ...base, action: 'crash', crashPct: customDraft.crashPct, recoveryYears: customDraft.recoveryYears || 0 }]);
    } else if (customDraft.action === 'cut_div') {
      setChanges((c) => [...c, { ...base, action: 'cut_div', cutPct: customDraft.cutPct, recoveryYears: customDraft.recoveryYears || 0 }]);
    }
    setCustomDraft({ action: customDraft.action, ticker: '', replaceWith: '', shares: 0, crashPct: 75, cutPct: 50, recoveryYears: 0 });
  };
  const removeChange = (id) => {
    setChanges((c) => {
      const removed = c.find((x) => x.id === id);
      if (removed?.preset_key) {
        setActivePresets((p) => ({ ...p, [removed.preset_key]: false }));
      }
      return c.filter((x) => x.id !== id);
    });
  };
  const resetAll = () => {
    setChanges([]);
    setSimResult(null);
    setActivePresets({});
    setPresetRecoveryYearsState(0);
    setYears(5);
    setGrowthRate(6);
    setPriceUpside(3);
    setReinvestDrip(true);
    setDripYears(null);
    setDca({ enabled: false, monthly: 200, tickers: [], split: false });
    setReverseMode(false);
    setBacktestResult(null);
  };

  // DCA toggle from the special preset card (inline)
  const toggleDcaTicker = (t) => {
    setDca((prev) => {
      const has = prev.tickers.includes(t);
      return { ...prev, tickers: has ? prev.tickers.filter((x) => x !== t) : [...prev.tickers, t] };
    });
  };

  const runSimulation = useCallback(async () => {
    setSimulating(true);
    try {
      // Build the effective `changes` list: include user-entered + DCA preset (if any tickers picked)
      const dcaChange = (dca.enabled && dca.monthly > 0 && dca.tickers.length > 0)
        ? {
            id: 'dca-preset',
            action: 'dca',
            preset_key: 'dca',
            dca_monthly: dca.monthly,
            dca_tickers: dca.tickers,
            dca_split: dca.split,
          }
        : null;
      const allChanges = dcaChange ? [...changes, dcaChange] : [...changes];

      // Pre-fetch any unknown tickers (changes + dca + replace targets)
      const needed = new Set();
      allChanges.forEach((c) => {
        if (c.ticker && !stockMap[c.ticker.toUpperCase()]) needed.add(c.ticker.toUpperCase());
        if (c.replaceWith && !stockMap[c.replaceWith.toUpperCase()]) needed.add(c.replaceWith.toUpperCase());
        (c.dca_tickers || []).forEach((t) => { if (!stockMap[t]) needed.add(t); });
      });
      let nextMap = stockMap;
      if (needed.size > 0) {
        const fetched = await Promise.all(
          Array.from(needed).map((t) =>
            axios.get(`${API}/stocks/${t}`, { params: { stale_ok: true } })
              .then((r) => r.data).catch(() => null)
          )
        );
        nextMap = { ...stockMap };
        fetched.forEach((s) => { if (s?.ticker) nextMap[s.ticker] = s; });
        setStockMap(nextMap);
      }

      const { simulated, summary, priceOverrides, divOverrides } = applyChanges(positions, allChanges, nextMap, years, priceUpside, growthRate, reinvestDrip);

      // Effective DRIP horizon for the What-If side. If the user
      // explicitly set dripYears via the "DRIP for 5y" preset, respect
      // that even when the time machine is set higher (e.g. 10y) so the
      // last (years - dripYears) years accumulate as cash.
      const effectiveDripYears = !reinvestDrip
        ? 0
        : (dripYears != null ? Math.min(dripYears, years) : years);

      // Project both scenarios:
      //  - Now: original positions, DRIP always OFF (per spec).
      //  - What If: simulated positions, DRIP per toggle (and partial
      //    DRIP horizon if "DRIP for 5y" preset is active).
      const nowProj = projectPortfolio({
        positions,
        stockMap: nextMap,
        years,
        priceGrowth: priceUpside,
        divGrowth: growthRate,
        dripYears: 0,
      });
      const whatProj = projectPortfolio({
        positions: simulated,
        stockMap: nextMap,
        years,
        priceGrowth: priceUpside,
        divGrowth: growthRate,
        dripYears: effectiveDripYears,
        priceOverrides,
        divOverrides,
      });

      // Year-by-year projection for the line chart
      const points = [];
      for (let y = 0; y <= years; y++) {
        const np = projectPortfolio({
          positions, stockMap: nextMap, years: y,
          priceGrowth: priceUpside, divGrowth: growthRate, dripYears: 0,
        });
        const wp = projectPortfolio({
          positions: simulated, stockMap: nextMap, years: y,
          priceGrowth: priceUpside, divGrowth: growthRate,
          dripYears: Math.min(effectiveDripYears, y),
          priceOverrides, divOverrides,
        });
        points.push({ year: y, Now: np.income, 'What If': wp.income });
      }

      setSimResult({
        simulated,
        summary,
        nowMetrics: nowProj,
        whatIfMetrics: whatProj,
        projectionPoints: points,
        params: {
          years, growthRate, priceUpside,
          drip: reinvestDrip,
          dripYears: effectiveDripYears,
        },
      });
      setLastAppliedKey(currentConfigKeyRef.current);
    } catch (e) {
      toast.error('Simulation failed', { description: e?.message || 'Try again.' });
    } finally {
      setSimulating(false);
    }
  }, [changes, positions, stockMap, years, priceUpside, growthRate, reinvestDrip, dripYears, dca]);

  // Reverse-mode backtest: replay real Yahoo monthly prices + dividends
  // over the last `years` years using the user's CURRENT share count as
  // the starting position N years ago. DRIP reinvests dividends at that
  // month's close; DCA buys monthly at that month's close. Returns per-
  // year income + ending value + cumulative dividends received.
  const runBacktest = useCallback(async () => {
    if (positions.length === 0) {
      toast.info('Add positions first to run a backtest.');
      return;
    }
    setSimulating(true);
    try {
      const userTickers = Array.from(new Set([
        ...positions.map((p) => p.ticker),
        ...(dca.enabled ? (dca.tickers || []) : []),
      ]));
      // Benchmarks: two flagship dividend ETFs. Piggybacked into the
      // same /api/whatif/backtest call so the overlay on the Portfolio
      // Value chart costs zero additional round trips.
      const BENCHMARK_TICKERS = ['SCHD', 'VYM'];
      const tickers = Array.from(new Set([...userTickers, ...BENCHMARK_TICKERS]));
      // Reverse Mode always fetches the maximum 5-year window so the
      // results-panel slider can slice 1y→5y without refetching.
      const MAX_BACKTEST_YEARS = 5;
      const res = await axios.post(
        `${API}/whatif/backtest`,
        { tickers, years: MAX_BACKTEST_YEARS },
        { withCredentials: true },
      );
      const series = res.data?.series || {};

      // Sanity check: need at least 2 months of aligned history to do
      // anything meaningful.
      const allMonths = new Set();
      Object.values(series).forEach((arr) => { (arr || []).forEach((m) => allMonths.add(m.date)); });
      if (allMonths.size < 2) {
        toast.error('Not enough historical data returned for a backtest.');
        setSimulating(false);
        return;
      }

      // Initial share counts by ticker (user's current position today).
      const initialShares = {};
      positions.forEach((p) => {
        initialShares[p.ticker] = (initialShares[p.ticker] || 0) + (p.shares || 0);
      });

      // Stash raw series + config in backtestResult so the
      // BacktestResultsPanel can recompute locally on its view-years
      // slider without a second API call.
      setBacktestResult({
        series,
        initialShares,
        dcaConfig: { ...dca },
        reinvestDrip,
        dripYears,
        maxYears: MAX_BACKTEST_YEARS,
        drip: reinvestDrip,
        dcaUsed: !!(dca.enabled && dca.monthly > 0 && (dca.tickers || []).length > 0),
        benchmarkTickers: BENCHMARK_TICKERS,
        userTickers,
      });
      setLastAppliedKey(currentConfigKeyRef.current);
      toast.success('Backtest complete', {
        description: `Fetched ${allMonths.size} months of real history.`,
      });
    } catch (e) {
      toast.error('Backtest failed', { description: e?.response?.data?.detail || e?.message || 'Try again.' });
    } finally {
      setSimulating(false);
    }
  }, [positions, reinvestDrip, dripYears, dca]);

  const sectorPalette = useMemo(() => {
    const set = new Set();
    (simResult?.nowMetrics.sectorRows || []).forEach((r) => set.add(r.sector));
    (simResult?.whatIfMetrics.sectorRows || []).forEach((r) => set.add(r.sector));
    const map = {};
    Array.from(set).forEach((s, i) => { map[s] = SECTOR_COLORS[i % SECTOR_COLORS.length]; });
    return map;
  }, [simResult]);

  // Config "snapshot key" — a stable JSON of every user-facing knob that
  // feeds the simulator. When the user last hit What If? we stash this
  // key in `lastAppliedKey`. If the live key equals the stashed one,
  // results are fresh (title = "Changes Applied"); otherwise title
  // flips to "Changes to be Applied" so the user knows they have
  // uncommitted edits.
  const currentConfigKey = useMemo(() => {
    // In Reverse Mode only DRIP + DCA + positions/changes actually feed
    // the historical replay — the forward-projection sliders (years,
    // growthRate, priceUpside, dripYears) don't. Exclude them from the
    // key so sliding them doesn't falsely flip the "Applied" title in
    // reverse mode.
    if (reverseMode) {
      return JSON.stringify({
        mode: 'reverse',
        // dripYears DOES affect the historical backtest (stops DRIP
        // reinvestment partway through the replay) so must be in the
        // key — otherwise the title wouldn't flip to "Changes to be
        // Applied" when the user drags the partial-DRIP sub-slider.
        changes, reinvestDrip, dripYears, dca, activePresets, presetRecoveryYears,
      });
    }
    return JSON.stringify({
      mode: 'forward',
      changes, years, growthRate, priceUpside, reinvestDrip, dripYears,
      dca, activePresets, presetRecoveryYears,
    });
  }, [changes, years, growthRate, priceUpside, reinvestDrip, dripYears, reverseMode, dca, activePresets, presetRecoveryYears]);
  // Keep ref in sync so the async run callbacks capture the latest key.
  useEffect(() => { currentConfigKeyRef.current = currentConfigKey; }, [currentConfigKey]);
  const isApplied = lastAppliedKey != null && lastAppliedKey === currentConfigKey;

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Hero-row trigger: only on md+ screens AND when no sim/backtest
  // result is on screen. When true, the three movable scenario cards
  // (DCA / Add-Remove / Custom Changes) render side-by-side inside
  // the empty-state panel instead of stacked in the left column.
  // As soon as a result lands (or the user shrinks below md), they
  // snap back to the left column. ONLY ONE instance of each card
  // mounts at any moment — the choice is made here in JS to avoid
  // duplicate `data-testid`s and double-mounted controlled inputs.
  const showHero = isMdUp && !simResult && !backtestResult;

  return (
    <div className="min-h-screen min-h-[100svh] pb-12" data-testid="whatif-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <BackOrHomeButton testid="whatif-back-or-home-btn" />
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-500" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="whatif-title">
              What If? <span className="text-muted-foreground text-base font-normal">Portfolio Simulator</span>
            </h1>
          </div>
          {/* Phase C (May 2026) — pin/re-pin live prices without losing
              the user's staged scenarios. Hidden until stockMap loads. */}
          <div className="w-24 hidden sm:flex justify-end">
            {Object.keys(stockMap).length > 0 && (
              <RepinPriceButton
                onClick={handleRefreshPrices}
                pinnedAt={pinnedAt}
                busy={refreshingPrices}
                testId="whatif-refresh-price"
              />
            )}
          </div>
        </div>

        {/* Saved Scenarios bar — cross-device, max 5 per user. Mirrors the
            Recently-Compared pattern: re-saving the same name overwrites,
            and the oldest is evicted past 5. Ideal for A/B-comparing
            different DCA / DRIP / horizon assumptions. */}
        <div
          className="rounded-xl border border-border bg-card/60 px-3 py-2 mb-4 flex items-center gap-2 flex-wrap"
          data-testid="whatif-saves-bar"
        >
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
            Saved Scenarios
          </span>
          {savedScenarios.length === 0 ? (
            <span className="text-[11px] text-muted-foreground italic">None yet — build one below and save it.</span>
          ) : (
            savedScenarios.map((s) => {
              const isBacktestSave = !!(s.scenario && s.scenario.reverseMode);
              return (
              <span
                key={s.name}
                className={`inline-flex items-center gap-1 rounded-full pl-2 pr-0.5 py-0.5 text-[11px] font-semibold ${
                  isBacktestSave
                    ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 ring-1 ring-amber-400/40'
                    : 'bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300'
                }`}
                data-testid={`whatif-saved-pill-${s.name}`}
              >
                {isBacktestSave && (
                  <History
                    className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0"
                    data-testid={`whatif-saved-backtest-badge-${s.name}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleLoadScenario(s)}
                  className="hover:underline max-w-[140px] truncate"
                  title={`Load "${s.name}"${isBacktestSave ? ' — Reverse Mode backtest' : ''} — saved ${new Date(s.updated_at).toLocaleDateString()}`}
                  data-testid={`whatif-saved-load-${s.name}`}
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  onClick={() => handleShareScenario(s)}
                  className={`rounded-full p-0.5 hover:bg-sky-200/60 dark:hover:bg-sky-900/40 ${
                    isBacktestSave
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-violet-600 dark:text-violet-400'
                  }`}
                  title={`Copy shareable link for "${s.name}"`}
                  data-testid={`whatif-saved-share-${s.name}`}
                >
                  <Link2 className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteScenario(s.name)}
                  className={`rounded-full p-0.5 hover:bg-rose-200/60 dark:hover:bg-rose-900/40 ${
                    isBacktestSave
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-violet-600 dark:text-violet-400'
                  }`}
                  title={`Delete "${s.name}"`}
                  data-testid={`whatif-saved-delete-${s.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              );
            })
          )}
          <button
            type="button"
            onClick={() => setShowSaveDialog((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="whatif-save-toggle"
          >
            <Plus className="w-3 h-3" /> Save current
          </button>
          {showSaveDialog && (
            <div
              className="basis-full flex items-center gap-2 mt-1.5 pt-1.5 border-t border-border/40"
              data-testid="whatif-save-dialog"
            >
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. 5y DRIP $200/mo split"
                className="h-8 text-xs flex-1"
                maxLength={60}
                data-testid="whatif-save-name"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveScenario(); }}
              />
              <Button
                size="sm"
                onClick={handleSaveScenario}
                className="bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs"
                data-testid="whatif-save-confirm"
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowSaveDialog(false); setSaveName(''); }}
                className="h-8 text-xs"
                data-testid="whatif-save-cancel"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {positions.length === 0 ? (
          <div className="rounded-xl border border-border bg-card px-6 py-10 text-center" data-testid="whatif-empty-state">
            <Sparkles className="w-10 h-10 text-violet-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-1">Add a position first to simulate</h2>
            <p className="text-sm text-muted-foreground mb-4">
              The What-If simulator runs scenarios on top of your real portfolio.
            </p>
            <Link to="/" className="inline-flex"><Button variant="outline">Browse stocks</Button></Link>
          </div>
        ) : (
          <>
            {/* Fresh-data fetch banner — shows while any ticker is being
                force-refreshed (after Add / Replace / swap-king preset).
                Gives the user explicit signal that live data is on the
                way before they click What If?. */}
            {prefetchingTickers.size > 0 && (
              <div
                className="mb-2 rounded-lg border border-sky-200 dark:border-sky-800/60 bg-sky-50/80 dark:bg-sky-950/30 px-3 py-1.5 flex items-center gap-2 text-[11px] text-sky-800 dark:text-sky-300 font-medium"
                data-testid="whatif-prefetching-banner"
              >
                <span className="inline-block w-3 h-3 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                <span>
                  Fetching fresh data for{' '}
                  <span className="font-bold font-mono">
                    {Array.from(prefetchingTickers).join(', ')}
                  </span>
                  {' '}before you run — will fall back to cached data if Massive fails.
                </span>
              </div>
            )}
            {/* Top run bar — duplicates the primary action up top so users
                don't have to scroll down to the Time Machine card each
                time they tweak a setting. Sticky on all viewports so it
                stays visible while scrolling through the builder. */}
            <div
              className="sticky top-14 z-30 mb-3 rounded-xl border border-violet-300/50 dark:border-violet-700/40 bg-background/95 backdrop-blur px-3 py-2 flex items-center gap-2 shadow-sm"
              data-testid="whatif-top-run-bar"
            >
              <Button
                onClick={async () => {
                  await (reverseMode ? runBacktest() : runSimulation());
                  flashJustRan();
                  // Phone-only scroll: on narrow viewports the result
                  // panels are far below the fold. Pull them into view
                  // so users see the sim actually ran.
                  scrollToResults();
                }}
                disabled={simulating}
                size="sm"
                className={`flex-1 font-bold h-9 transition-all active:scale-95 ${
                  justRan ? 'whatif-just-ran' : ''
                } ${
                  reverseMode
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-violet-600 hover:bg-violet-700 text-white'
                }`}
                data-testid="whatif-run-btn-top"
              >
                {simulating
                  ? <RotateCcw className="w-4 h-4 mr-1 animate-spin" />
                  : (reverseMode
                    ? <History className="w-4 h-4 mr-1" />
                    : <Sparkles className={`w-4 h-4 mr-1 ${justRan ? 'animate-ping' : ''}`} />)
                }
                {simulating
                  ? (reverseMode ? 'Backtesting…' : 'Running…')
                  : (justRan
                    ? (reverseMode ? 'Backtest Ran! 🔮' : 'Simulated! ✨')
                    : (reverseMode ? 'Run Backtest 🔮' : 'What If? 🔮')
                  )
                }
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={resetAll}
                className="h-9 text-xs shrink-0"
                data-testid="whatif-reset-btn-top"
              >
                <RotateCcw className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* LEFT — Scenario builder */}
            <div className="lg:col-span-1 space-y-3">
              {/* Quick scenario checkboxes */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-builder">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-violet-500" />
                  <h2 className="text-sm font-bold uppercase tracking-wider">Quick Scenarios</h2>
                  <span className="text-[10px] text-muted-foreground ml-auto">multi-select</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PRESET_LABELS).map(([key, label]) => (
                    <PresetCheckbox
                      key={key}
                      icon={PRESET_ICONS[key]}
                      label={label}
                      checked={!!activePresets[key]}
                      onClick={() => togglePreset(key)}
                      testId={`preset-${key}`}
                    />
                  ))}
                </div>

                {(activePresets.crash || activePresets['cut-top-yielder']) && (
                  <div
                    className="pt-2 border-t border-border/40 flex items-center gap-1.5 flex-wrap"
                    data-testid="whatif-preset-recovery"
                  >
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
                      Recovery
                    </span>
                    <span className="text-[11px] text-muted-foreground font-medium leading-relaxed">
                      (bounce-back for crash / cut presets)
                    </span>
                    <div className="ml-auto flex gap-1 flex-wrap">
                      {[
                        { v: 0, lbl: 'Permanent' },
                        { v: 3, lbl: '3y' },
                        { v: 5, lbl: '5y' },
                        { v: 10, lbl: '10y' },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setPresetRecovery(opt.v)}
                          className={`px-2 py-0.5 rounded text-[11px] font-mono font-semibold transition-colors ${
                            presetRecoveryYears === opt.v
                              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60'
                              : 'text-muted-foreground hover:bg-accent'
                          }`}
                          data-testid={`whatif-preset-recovery-${opt.v}`}
                        >
                          {opt.lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* TIME_MACHINE_BLOCK — moved to sit between Quick
                  Scenarios and the DCA card per user spec so the
                  timeframe + growth sliders are visible before the user
                  starts tuning DCA / Add-Remove / Custom Changes. */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-globals">
                <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-emerald-500" /> Time Machine
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold ml-auto normal-case tracking-normal" data-testid="whatif-time-machine-always-on">always on</span>
                </h2>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Years to project</label>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {[1, 5, 10, 20, 30].map((y) => (
                      <button
                        key={y}
                        type="button"
                        onClick={() => setYears(y)}
                        className={`px-2 py-1 rounded text-xs font-mono font-semibold transition-colors ${
                          years === y
                            ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60'
                            : 'text-muted-foreground hover:bg-accent'
                        }`}
                        data-testid={`whatif-years-${y}`}
                      >
                        {y}y
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Annual dividend growth: <span className="text-foreground font-mono font-semibold">{growthRate}%</span>
                  </label>
                  <input
                    type="range" min="0" max="15" step="0.5"
                    value={growthRate}
                    onChange={(e) => setGrowthRate(parseFloat(e.target.value))}
                    className="w-full mt-1 accent-violet-500"
                    data-testid="whatif-growth-slider"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Stock price upside / downside:{' '}
                    <span className={`font-mono font-semibold ${priceUpside < 0 ? 'text-rose-500' : 'text-foreground'}`}>
                      {priceUpside >= 0 ? '+' : ''}{priceUpside}%
                    </span>
                    <span className="text-[9px] text-muted-foreground font-normal normal-case ml-1">/yr</span>
                  </label>
                  <input
                    type="range" min="-15" max="15" step="0.5"
                    value={priceUpside}
                    onChange={(e) => setPriceUpside(parseFloat(e.target.value))}
                    className="w-full mt-1 accent-fuchsia-500"
                    data-testid="whatif-price-slider"
                    title="Set negative for a market downturn scenario"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reinvestDrip}
                    onChange={(e) => setReinvestDrip(e.target.checked)}
                    data-testid="whatif-drip-toggle"
                  />
                  <span className="text-xs">Reinvest dividends (DRIP) on the right</span>
                </label>

                {/* Reverse Mode — runs a historical backtest over the last
                    N years instead of projecting forward. Uses real Yahoo
                    monthly prices + dividends. */}
                <label
                  className="flex items-start gap-2 cursor-pointer pt-2 border-t border-border/40"
                  data-testid="whatif-reverse-toggle-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={reverseMode}
                    onChange={(e) => { setReverseMode(e.target.checked); setBacktestResult(null); }}
                    className="mt-0.5"
                    data-testid="whatif-reverse-toggle"
                  />
                  <span className="text-xs leading-snug">
                    <span className="font-semibold inline-flex items-center gap-1">
                      <History className="w-3.5 h-3.5 text-amber-500" />
                      Reverse Mode 🔮
                    </span>
                    <span className="block text-[10px] text-muted-foreground mt-0.5">
                      Backtest with real closing prices from the last 1y to 5y — validates your DCA / DRIP discipline against actual market history. 5y is the starting setting.
                    </span>
                  </span>
                </label>
              </div>

              {/* The next three cards (DCA, Add/Remove, Custom Changes)
                  are MOVABLE — they live here in the left column on
                  phones (always) and on desktops AFTER a sim runs.
                  Before a sim runs on desktop they hop out of this
                  column and render side-by-side inside the empty-state
                  hero panel. See `showHero` flag. */}
              {!showHero && (
              <>
              {/* DCA preset — 2-cell wide block */}
              <DcaPresetCard
                tickers={effectiveTickers}
                stockMap={stockMap}
                value={dca}
                onChange={setDca}
              />

              {/* Custom changes */}
              {/* Add or Remove a Position — toggles between Add and
                  Remove modes with one apply button. Keeps the UI compact
                  so the left panel doesn't grow with each entry — all
                  committed changes appear in the Changes Applied panel
                  on the right. */}<div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-add-remove">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <h2 className="text-sm font-bold uppercase tracking-wider">Add / Remove Position</h2>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setAddRemoveMode('add')}
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors ${
                        addRemoveMode === 'add'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                      }`}
                      data-testid="whatif-add-position-btn"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddRemoveMode('remove')}
                      disabled={effectiveTickers.length === 0}
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors disabled:opacity-50 ${
                        addRemoveMode === 'remove'
                          ? 'bg-rose-500 text-white'
                          : 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300'
                      }`}
                      data-testid="whatif-remove-position-btn"
                    >
                      <Minus className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
                {addRemoveMode === 'add' ? (
                  <>
                    <TickerInput
                      value={addRemoveDraft.ticker}
                      onChange={(t) => setAddRemoveDraft((d) => ({ ...d, ticker: t }))}
                      placeholder="Search ticker to add…"
                      testId="whatif-add-ticker"
                    />
                    <Input
                      type="number" min="0" step="0.01"
                      value={addRemoveDraft.shares || ''}
                      onChange={(e) => setAddRemoveDraft((d) => ({ ...d, shares: parseFloat(e.target.value) || 0 }))}
                      placeholder="Shares"
                      className="h-8 text-xs font-mono"
                      data-testid="whatif-add-shares"
                    />
                  </>
                ) : (
                  <Select
                    value={addRemoveDraft.ticker || ''}
                    onValueChange={(v) => setAddRemoveDraft((d) => ({ ...d, ticker: v }))}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="whatif-remove-ticker-dropdown">
                      <SelectValue placeholder="Select ticker to remove…" />
                    </SelectTrigger>
                    <SelectContent>
                      {effectiveTickers.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  size="sm"
                  onClick={applyAddRemove}
                  disabled={!addRemoveDraft.ticker || (addRemoveMode === 'add' && (!addRemoveDraft.shares || addRemoveDraft.shares <= 0))}
                  className={`w-full h-8 text-xs font-bold ${
                    addRemoveMode === 'add'
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-rose-600 hover:bg-rose-700 text-white'
                  }`}
                  data-testid="whatif-add-remove-apply"
                >
                  Apply {addRemoveMode === 'add' ? 'Add' : 'Remove'}
                </Button>
              </div>

              {/* Custom Changes — one draft form per session, committed
                  into the changes list with Apply. Action select shows
                  only modify/replace/crash/cut_div (add/remove live in the
                  box above). */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-changes">
                <h2 className="text-sm font-bold uppercase tracking-wider">Custom Changes</h2>
                <Select value={customDraft.action} onValueChange={(v) => setCustomDraft((d) => ({ ...d, action: v, ticker: '', replaceWith: '' }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="whatif-custom-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modify">{ACTION_LABELS.modify}</SelectItem>
                    <SelectItem value="replace">{ACTION_LABELS.replace}</SelectItem>
                    <SelectItem value="crash">{ACTION_LABELS.crash}</SelectItem>
                    <SelectItem value="cut_div">{ACTION_LABELS.cut_div}</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={customDraft.ticker || ''}
                  onValueChange={(v) => setCustomDraft((d) => ({ ...d, ticker: v }))}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="whatif-custom-ticker-dropdown">
                    <SelectValue placeholder={customDraft.action === 'replace' ? 'From ticker…' : 'Pick ticker…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {effectiveTickers.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {customDraft.action === 'replace' && (
                  <TickerInput
                    value={customDraft.replaceWith}
                    onChange={(t) => setCustomDraft((d) => ({ ...d, replaceWith: t }))}
                    placeholder="To: search ticker…"
                    testId="whatif-custom-replacewith"
                  />
                )}

                {customDraft.action === 'modify' && (
                  <Input
                    type="number" min="0" step="0.01"
                    value={customDraft.shares || ''}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, shares: parseFloat(e.target.value) || 0 }))}
                    placeholder="New share count"
                    className="h-8 text-xs font-mono"
                    data-testid="whatif-custom-shares"
                  />
                )}

                {customDraft.action === 'crash' && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Crash size (price + dividend): <span className="font-mono text-rose-600 dark:text-rose-400 font-semibold">−{customDraft.crashPct}%</span>
                    </label>
                    <input
                      type="range" min="10" max="95" step="5"
                      value={customDraft.crashPct}
                      onChange={(e) => setCustomDraft((d) => ({ ...d, crashPct: parseFloat(e.target.value) }))}
                      className="w-full mt-0.5 accent-rose-500"
                      data-testid="whatif-custom-crash-pct"
                    />
                  </div>
                )}

                {customDraft.action === 'cut_div' && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Dividend cut: <span className="font-mono text-rose-600 dark:text-rose-400 font-semibold">−{customDraft.cutPct}%</span>
                    </label>
                    <input
                      type="range" min="10" max="100" step="5"
                      value={customDraft.cutPct}
                      onChange={(e) => setCustomDraft((d) => ({ ...d, cutPct: parseFloat(e.target.value) }))}
                      className="w-full mt-0.5 accent-rose-500"
                      data-testid="whatif-custom-cut-pct"
                    />
                  </div>
                )}

                {(customDraft.action === 'crash' || customDraft.action === 'cut_div') && (
                  <div data-testid="whatif-custom-recovery">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Recovery period
                      <span className="ml-1 font-medium normal-case tracking-normal text-[11px]">
                        (permanent = no bounce-back)
                      </span>
                    </label>
                    <div className="flex gap-1 mt-0.5 flex-wrap">
                      {[
                        { v: 0, lbl: 'Permanent' },
                        { v: 3, lbl: '3y' },
                        { v: 5, lbl: '5y' },
                        { v: 10, lbl: '10y' },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setCustomDraft((d) => ({ ...d, recoveryYears: opt.v }))}
                          className={`px-2 py-1 rounded text-[11px] font-mono font-semibold transition-colors ${
                            (customDraft.recoveryYears || 0) === opt.v
                              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60'
                              : 'text-muted-foreground hover:bg-accent'
                          }`}
                          data-testid={`whatif-custom-recovery-${opt.v}`}
                        >
                          {opt.lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={applyCustomChange}
                  disabled={!customDraft.ticker || (customDraft.action === 'replace' && !customDraft.replaceWith) || (customDraft.action === 'modify' && (!customDraft.shares || customDraft.shares < 0))}
                  className="w-full h-8 text-xs font-bold bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="whatif-custom-apply"
                >
                  Apply Change
                </Button>
              </div>
              </>
              )}
              {/* Time Machine moved — now lives above the DCA card,
                  right after Quick Scenarios. See `TIME_MACHINE_BLOCK`
                  marker further up in this component. */}

              <div className="flex gap-2 mb-6">
                <Button
                  onClick={async () => {
                    await (reverseMode ? runBacktest() : runSimulation());
                    flashJustRan();
                    scrollToResults();
                  }}
                  disabled={simulating}
                  className={`flex-1 font-bold transition-all active:scale-95 ${
                    justRan ? 'whatif-just-ran' : ''
                  } ${
                    reverseMode
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-violet-600 hover:bg-violet-700 text-white'
                  }`}
                  data-testid="whatif-run-btn"
                >
                  {simulating
                    ? <RotateCcw className="w-4 h-4 mr-1 animate-spin" />
                    : (reverseMode
                      ? <History className="w-4 h-4 mr-1" />
                      : <Sparkles className={`w-4 h-4 mr-1 ${justRan ? 'animate-ping' : ''}`} />)
                  }
                  {simulating
                    ? (reverseMode ? 'Backtesting…' : 'Running…')
                    : (justRan
                      ? (reverseMode ? 'Backtest Ran! 🔮' : 'Simulated! ✨')
                      : (reverseMode ? 'Run Backtest 🔮' : 'What If? 🔮')
                    )
                  }
                </Button>
                <Button variant="outline" onClick={resetAll} className="text-xs" data-testid="whatif-reset-btn">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            </div>

            {/* RIGHT — Results */}
            <div className="lg:col-span-2 space-y-4" ref={resultsRef}>
              {/* Live Changes Applied panel — always visible, shows the
                  current `changes` list + DCA (when enabled) so users
                  see committed changes immediately after each Apply.
                  Collapsible when > 3 entries. */}
              <LiveChangesApplied
                changes={changes}
                dca={dca}
                stockMap={stockMap}
                showAll={showAllChanges}
                setShowAll={setShowAllChanges}
                onRemove={removeChange}
                onClearAll={() => setChanges([])}
                years={years}
                growthRate={growthRate}
                priceUpside={priceUpside}
                reinvestDrip={reinvestDrip}
                dripYears={dripYears}
                reverseMode={reverseMode}
                isApplied={isApplied}
                setYears={setYears}
                setGrowthRate={setGrowthRate}
                setPriceUpside={setPriceUpside}
                setReinvestDrip={setReinvestDrip}
                setDripYears={setDripYears}
              />
              {!simResult && !backtestResult ? (
                <div className="rounded-xl border-2 border-dashed border-border bg-card/50 px-6 py-12 text-center" data-testid="whatif-results-empty">
                  <Sparkles className="w-10 h-10 text-violet-500 mx-auto mb-3 animate-pulse" />
                  <h2 className="text-lg font-bold mb-1">Pick a scenario or add a change</h2>
                  <p className="text-sm text-muted-foreground">
                    Then click <span className="font-semibold text-violet-600 dark:text-violet-400">What If?</span>{' '}
                    to see your portfolio in a parallel universe.
                  </p>
                  {/* Hero row — md+ ONLY. Two-column layout:
                      LEFT  = DCA card (taller, fills column on its own)
                      RIGHT = vertical stack of Add/Remove (top) +
                              Custom Changes (directly under it). */}
                  {showHero && (
                    <div
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 text-left items-start"
                      data-testid="whatif-hero-row"
                    >
                      <div>
                        <DcaPresetCard
                          tickers={effectiveTickers}
                          stockMap={stockMap}
                          value={dca}
                          onChange={setDca}
                        />
                      </div>
                      <div className="space-y-4">
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-add-remove">
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <h2 className="text-sm font-bold uppercase tracking-wider">Add / Remove Position</h2>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setAddRemoveMode('add')}
                                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors ${
                                  addRemoveMode === 'add'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                                }`}
                                data-testid="whatif-add-position-btn"
                              >
                                <Plus className="w-3 h-3" /> Add
                              </button>
                              <button
                                type="button"
                                onClick={() => setAddRemoveMode('remove')}
                                disabled={effectiveTickers.length === 0}
                                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors disabled:opacity-50 ${
                                  addRemoveMode === 'remove'
                                    ? 'bg-rose-500 text-white'
                                    : 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300'
                                }`}
                                data-testid="whatif-remove-position-btn"
                              >
                                <Minus className="w-3 h-3" /> Remove
                              </button>
                            </div>
                          </div>
                          {addRemoveMode === 'add' ? (
                            <>
                              <TickerInput
                                value={addRemoveDraft.ticker}
                                onChange={(t) => setAddRemoveDraft((d) => ({ ...d, ticker: t }))}
                                placeholder="Search ticker to add…"
                                testId="whatif-add-ticker"
                              />
                              <Input
                                type="number" min="0" step="0.01"
                                value={addRemoveDraft.shares || ''}
                                onChange={(e) => setAddRemoveDraft((d) => ({ ...d, shares: parseFloat(e.target.value) || 0 }))}
                                placeholder="Shares"
                                className="h-8 text-xs font-mono"
                                data-testid="whatif-add-shares"
                              />
                            </>
                          ) : (
                            <Select
                              value={addRemoveDraft.ticker || ''}
                              onValueChange={(v) => setAddRemoveDraft((d) => ({ ...d, ticker: v }))}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid="whatif-remove-ticker-dropdown">
                                <SelectValue placeholder="Select ticker to remove…" />
                              </SelectTrigger>
                              <SelectContent>
                                {effectiveTickers.map((t) => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Button
                            size="sm"
                            onClick={applyAddRemove}
                            disabled={!addRemoveDraft.ticker || (addRemoveMode === 'add' && (!addRemoveDraft.shares || addRemoveDraft.shares <= 0))}
                            className={`w-full h-8 text-xs font-bold ${
                              addRemoveMode === 'add'
                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                : 'bg-rose-600 hover:bg-rose-700 text-white'
                            }`}
                            data-testid="whatif-add-remove-apply"
                          >
                            Apply {addRemoveMode === 'add' ? 'Add' : 'Remove'}
                          </Button>
                        </div>
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="whatif-changes">
                          <h2 className="text-sm font-bold uppercase tracking-wider">Custom Changes</h2>
                          <Select value={customDraft.action} onValueChange={(v) => setCustomDraft((d) => ({ ...d, action: v, ticker: '', replaceWith: '' }))}>
                            <SelectTrigger className="h-8 text-xs" data-testid="whatif-custom-action">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="modify">{ACTION_LABELS.modify}</SelectItem>
                              <SelectItem value="replace">{ACTION_LABELS.replace}</SelectItem>
                              <SelectItem value="crash">{ACTION_LABELS.crash}</SelectItem>
                              <SelectItem value="cut_div">{ACTION_LABELS.cut_div}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select
                            value={customDraft.ticker || ''}
                            onValueChange={(v) => setCustomDraft((d) => ({ ...d, ticker: v }))}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="whatif-custom-ticker-dropdown">
                              <SelectValue placeholder={customDraft.action === 'replace' ? 'From ticker…' : 'Pick ticker…'} />
                            </SelectTrigger>
                            <SelectContent>
                              {effectiveTickers.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {customDraft.action === 'replace' && (
                            <TickerInput
                              value={customDraft.replaceWith}
                              onChange={(t) => setCustomDraft((d) => ({ ...d, replaceWith: t }))}
                              placeholder="To: search ticker…"
                              testId="whatif-custom-replacewith"
                            />
                          )}
                          {customDraft.action === 'modify' && (
                            <Input
                              type="number" min="0" step="0.01"
                              value={customDraft.shares || ''}
                              onChange={(e) => setCustomDraft((d) => ({ ...d, shares: parseFloat(e.target.value) || 0 }))}
                              placeholder="New share count"
                              className="h-8 text-xs font-mono"
                              data-testid="whatif-custom-shares"
                            />
                          )}
                          {customDraft.action === 'crash' && (
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                Crash size (price + dividend): <span className="font-mono text-rose-600 dark:text-rose-400 font-semibold">−{customDraft.crashPct}%</span>
                              </label>
                              <input
                                type="range" min="10" max="95" step="5"
                                value={customDraft.crashPct}
                                onChange={(e) => setCustomDraft((d) => ({ ...d, crashPct: parseFloat(e.target.value) }))}
                                className="w-full mt-0.5 accent-rose-500"
                                data-testid="whatif-custom-crash-pct"
                              />
                            </div>
                          )}
                          {customDraft.action === 'cut_div' && (
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                Dividend cut: <span className="font-mono text-rose-600 dark:text-rose-400 font-semibold">−{customDraft.cutPct}%</span>
                              </label>
                              <input
                                type="range" min="10" max="100" step="5"
                                value={customDraft.cutPct}
                                onChange={(e) => setCustomDraft((d) => ({ ...d, cutPct: parseFloat(e.target.value) }))}
                                className="w-full mt-0.5 accent-rose-500"
                                data-testid="whatif-custom-cut-pct"
                              />
                            </div>
                          )}
                          {(customDraft.action === 'crash' || customDraft.action === 'cut_div') && (
                            <div data-testid="whatif-custom-recovery">
                              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                Recovery period
                                <span className="ml-1 font-medium normal-case tracking-normal text-[11px]">
                                  (permanent = no bounce-back)
                                </span>
                              </label>
                              <div className="flex gap-1 mt-0.5 flex-wrap">
                                {[
                                  { v: 0, lbl: 'Permanent' },
                                  { v: 3, lbl: '3y' },
                                  { v: 5, lbl: '5y' },
                                  { v: 10, lbl: '10y' },
                                ].map((opt) => (
                                  <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => setCustomDraft((d) => ({ ...d, recoveryYears: opt.v }))}
                                    className={`px-2 py-1 rounded text-[11px] font-mono font-semibold transition-colors ${
                                      (customDraft.recoveryYears || 0) === opt.v
                                        ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60'
                                        : 'text-muted-foreground hover:bg-accent'
                                    }`}
                                    data-testid={`whatif-custom-recovery-${opt.v}`}
                                  >
                                    {opt.lbl}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <Button
                            size="sm"
                            onClick={applyCustomChange}
                            disabled={!customDraft.ticker || (customDraft.action === 'replace' && !customDraft.replaceWith) || (customDraft.action === 'modify' && (!customDraft.shares || customDraft.shares < 0))}
                            className="w-full h-8 text-xs font-bold bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid="whatif-custom-apply"
                          >
                            Apply Change
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : reverseMode && backtestResult ? (
                <BacktestResultsPanel result={backtestResult} stockMap={stockMap} />
              ) : simResult ? (
                <SimResultsPanel
                  result={simResult}
                  sectorPalette={sectorPalette}
                  stockMap={stockMap}
                />
              ) : null}
            </div>
          </div>
          </>
        )}

        <SiteDisclaimer />
      </div>

      {/* Glossary-leave confirmation modal. User can only close via an
          action button or the X — clicking outside does nothing. */}
      {glossaryModal.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
          data-testid="whatif-glossary-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-md rounded-xl border border-border bg-background shadow-2xl p-5 space-y-3">
            <button
              type="button"
              onClick={() => setGlossaryModal({ open: false, target: '/glossary' })}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-accent"
              title="Cancel"
              data-testid="whatif-glossary-modal-x"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" /> Leave the simulator?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your current What-If settings (changes, presets, DCA, time machine) will be lost if you
              navigate away. What would you like to do?
            </p>
            <div className="space-y-2 pt-2">
              <Button
                onClick={async () => {
                  // Autosave name — use ISO-style timestamp with
                  // hyphens/colons-replaced-with-dashes so the name
                  // contains no forward slashes (slashes break the
                  // DELETE /whatif-saves/{name} path routing even when
                  // URI-encoded, because upstream proxies decode %2F
                  // back to / before FastAPI sees it).
                  const iso = new Date().toISOString().replace(/:/g, '-').replace(/\..+$/, '');
                  const autoName = `_autosave ${iso}`;
                  // Include everything the Time Machine + Reverse Mode
                  // depend on: presetRecoveryYears was missing here
                  // before, and reverseMode is force-true whenever a
                  // backtest is on-screen so the reloaded scenario
                  // opens in Reverse Mode with "Run Backtest" ready.
                  const effectiveReverseMode = reverseMode || backtestResult != null;
                  try {
                    await axios.post(`${API}/whatif-saves`, {
                      name: autoName,
                      scenario: {
                        changes, years, growthRate, priceUpside, reinvestDrip, dripYears,
                        activePresets, dca, presetRecoveryYears,
                        reverseMode: effectiveReverseMode,
                      },
                    }, { withCredentials: true });
                    toast.success('Scenario auto-saved', { description: autoName });
                  } catch {
                    toast.error('Auto-save failed — continuing anyway');
                  }
                  setGlossaryModal({ open: false, target: '/glossary' });
                  window.location.href = glossaryModal.target;
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="whatif-glossary-save-continue"
              >
                Save Settings and Continue
              </Button>
              <Button
                onClick={() => { window.open(glossaryModal.target, '_blank'); setGlossaryModal({ open: false, target: '/glossary' }); }}
                variant="outline"
                className="w-full"
                data-testid="whatif-glossary-newtab"
              >
                Open Glossary in New Tab
              </Button>
              <Button
                onClick={() => { setGlossaryModal({ open: false, target: '/glossary' }); window.location.href = glossaryModal.target; }}
                variant="outline"
                className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30"
                data-testid="whatif-glossary-continue"
              >
                Continue Without Saving
              </Button>
              <Button
                onClick={() => setGlossaryModal({ open: false, target: '/glossary' })}
                variant="ghost"
                className="w-full"
                data-testid="whatif-glossary-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PRESET_LABELS = {
  'boost-yield': '+$5K to top yielder',
  'replace-risky': 'Swap riskiest → King',
  'double-best': '2× best performer',
  'drip-5y': 'DRIP on for 5 years',
  'cut-top-yielder': 'Top yielder cuts div 50%',
  'crash': 'Top Yielder Stock Crash',
};
const PRESET_ICONS = {
  'boost-yield': Zap,
  'replace-risky': Shield,
  'double-best': TrendingUp,
  'drip-5y': Repeat,
  'cut-top-yielder': TrendingDown,
  'crash': AlertTriangle,
};

function PresetCheckbox({ icon: Icon, label, checked, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex items-center gap-1.5 rounded-lg border px-2 py-2 text-left transition-colors ${
        checked
          ? 'border-violet-500 bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-200'
          : 'border-border bg-card hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/40'
      }`}
      data-testid={testId}
      role="checkbox"
      aria-checked={checked}
    >
      <span className={`flex items-center justify-center w-4 h-4 rounded border ${
        checked ? 'bg-violet-500 border-violet-500 text-white' : 'border-border bg-background'
      }`}>
        {checked && <Check className="w-3 h-3" />}
      </span>
      <Icon className="w-3.5 h-3.5 text-violet-500 shrink-0" />
      <span className="text-[10px] font-semibold leading-tight flex-1">{label}</span>
    </button>
  );
}

// ----- DCA preset card --------------------------------------------------------
// Reusable editable "chip" that renders as a tappable pill and opens an
// InlinePopover with a small editor. Used on the baseline assumption row
// so the user can tweak years / DRIP / growth / price right from the
// Changes Applied panel without scrolling back to the Time Machine.
// Tiny SVG sparkline — no recharts dependency. Designed to slot inside
// the 3-way headline cards at roughly 80×24 with 1.5px stroke. Auto-
// normalises the given values to the box; if all values are equal, draws
// a flat midline.
function Sparkline({ values, color = '#f59e0b', width = 80, height = 24, strokeWidth = 1.5 }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const d = values
    .map((v, i) => {
      const x = (i * stepX).toFixed(2);
      // Invert Y because SVG y grows downward.
      const y = (height - ((v - min) / range) * (height - 2) - 1).toFixed(2);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BaselineChip({ label, testId, render }) {
  return (
    <InlinePopover
      testId={testId}
      className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-md bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 text-violet-700 dark:text-violet-300 font-mono font-semibold transition-colors align-baseline"
      ariaLabel={`Edit ${label}`}
      maxWidth={260}
      render={render}
    >
      {label}
    </InlinePopover>
  );
}

function BaselineChips({
  years, growthRate, priceUpside, reinvestDrip, dripYears, reverseMode,
  setYears, setGrowthRate, setPriceUpside, setReinvestDrip, setDripYears,
}) {
  const dripLabel = (() => {
    if (!reinvestDrip) return 'Off';
    if (dripYears == null || dripYears >= years) return `On (${years}y)`;
    if (dripYears <= 0) return 'Off';
    return `On ${dripYears}y → Off`;
  })();

  // ------- Editor render funcs -------
  const yearsEditor = ({ close }) => (
    <div className="space-y-2" data-testid="whatif-baseline-years-editor">
      <div className="text-[11px] font-bold text-foreground">Timeframe</div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={1}
          max={30}
          step={1}
          value={years}
          onChange={(e) => setYears(Number(e.target.value))}
          className="flex-1 accent-violet-500"
          data-testid="whatif-baseline-years-slider"
        />
        <span className="text-xs font-mono font-bold text-violet-600 dark:text-violet-400 w-10 text-right">{years}y</span>
      </div>
      <div className="flex gap-1 flex-wrap">
        {[1, 3, 5, 10, 20, 30].map((y) => (
          <button
            key={y}
            type="button"
            onClick={() => { setYears(y); close(); }}
            className={`px-2 py-0.5 rounded text-[11px] font-mono border ${years === y ? 'bg-violet-600 text-white border-violet-600' : 'border-border hover:bg-muted'}`}
            data-testid={`whatif-baseline-years-${y}`}
          >
            {y}y
          </button>
        ))}
      </div>
    </div>
  );

  const dripEditor = ({ close }) => (
    <div className="space-y-2" data-testid="whatif-baseline-drip-editor">
      <div className="text-[11px] font-bold text-foreground">Dividend Reinvestment (DRIP)</div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => { setReinvestDrip(true); setDripYears(null); close(); }}
          className={`flex-1 px-2 py-1 rounded text-[11px] font-semibold border ${reinvestDrip && (dripYears == null || dripYears >= years) ? 'bg-emerald-600 text-white border-emerald-600' : 'border-border hover:bg-muted'}`}
          data-testid="whatif-baseline-drip-full"
        >
          Full
        </button>
        <button
          type="button"
          onClick={() => { setReinvestDrip(false); setDripYears(null); close(); }}
          className={`flex-1 px-2 py-1 rounded text-[11px] font-semibold border ${!reinvestDrip ? 'bg-rose-600 text-white border-rose-600' : 'border-border hover:bg-muted'}`}
          data-testid="whatif-baseline-drip-off"
        >
          Off
        </button>
      </div>
      {reinvestDrip && (
        <div>
          <div className="text-[10px] text-muted-foreground mb-1">Partial: DRIP first N years, then cash out.</div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={years}
              step={1}
              value={dripYears == null ? years : dripYears}
              onChange={(e) => setDripYears(Number(e.target.value))}
              className="flex-1 accent-emerald-500"
              data-testid="whatif-baseline-drip-years-slider"
            />
            <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 w-12 text-right">
              {dripYears == null ? years : dripYears}y
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const growthEditor = ({ close }) => (
    <div className="space-y-2" data-testid="whatif-baseline-growth-editor">
      <div className="text-[11px] font-bold text-foreground">Annual Dividend Growth</div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={-10}
          max={20}
          step={0.5}
          value={growthRate}
          onChange={(e) => setGrowthRate(Number(e.target.value))}
          className="flex-1 accent-emerald-500"
          data-testid="whatif-baseline-growth-slider"
        />
        <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 w-14 text-right">{growthRate}%/yr</span>
      </div>
      <div className="flex gap-1 flex-wrap">
        {[0, 3, 6, 8, 10].map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => { setGrowthRate(g); close(); }}
            className={`px-2 py-0.5 rounded text-[11px] font-mono border ${growthRate === g ? 'bg-emerald-600 text-white border-emerald-600' : 'border-border hover:bg-muted'}`}
            data-testid={`whatif-baseline-growth-${g}`}
          >
            {g}%
          </button>
        ))}
      </div>
    </div>
  );

  const priceEditor = ({ close }) => (
    <div className="space-y-2" data-testid="whatif-baseline-price-editor">
      <div className="text-[11px] font-bold text-foreground">Annual Price Appreciation</div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={-20}
          max={20}
          step={0.5}
          value={priceUpside}
          onChange={(e) => setPriceUpside(Number(e.target.value))}
          className="flex-1 accent-amber-500"
          data-testid="whatif-baseline-price-slider"
        />
        <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400 w-14 text-right">
          {priceUpside >= 0 ? '+' : ''}{priceUpside}%/yr
        </span>
      </div>
      <div className="flex gap-1 flex-wrap">
        {[-5, 0, 3, 5, 8, 10].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => { setPriceUpside(p); close(); }}
            className={`px-2 py-0.5 rounded text-[11px] font-mono border ${priceUpside === p ? 'bg-amber-600 text-white border-amber-600' : 'border-border hover:bg-muted'}`}
            data-testid={`whatif-baseline-price-${p}`}
          >
            {p >= 0 ? '+' : ''}{p}%
          </button>
        ))}
      </div>
    </div>
  );

  if (reverseMode) {
    // In reverse mode only DRIP actually affects the historical replay.
    // The backtest window is controlled by the 1y-5y slider on the results
    // panel below, not by the `years` state — so we skip the years chip
    // to avoid confusion.
    return (
      <span className="inline-flex flex-wrap items-center gap-1 text-xs">
        <span className="italic">Backtest:</span>
        <span>real history · DRIP:</span>
        <BaselineChip label={dripLabel} testId="whatif-baseline-chip-drip" render={dripEditor} />
        <span className="text-muted-foreground/80">· window set on results slider below</span>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs">
      <span className="italic">Forecast:</span>
      <BaselineChip label={`${years}y`} testId="whatif-baseline-chip-years" render={yearsEditor} />
      <span>· DRIP:</span>
      <BaselineChip label={dripLabel} testId="whatif-baseline-chip-drip" render={dripEditor} />
      <span>· Div Growth</span>
      <BaselineChip label={`${growthRate}%/yr`} testId="whatif-baseline-chip-growth" render={growthEditor} />
      <span>· Price</span>
      <BaselineChip label={`${priceUpside >= 0 ? '+' : ''}${priceUpside}%/yr`} testId="whatif-baseline-chip-price" render={priceEditor} />
    </span>
  );
}

// ----- Live Changes Applied panel --------------------------------------------
// Shows the committed `changes` array live (and the DCA preset when enabled).
// When > 3 entries, collapses to first 3 + "View More ↓" button. Each row
// has an X to remove the individual change; a "Clear all" button wipes the
// whole list. Tickers render in blue (user spec).
function LiveChangesApplied({ changes, dca, stockMap, showAll, setShowAll, onRemove, onClearAll, years, growthRate, priceUpside, reinvestDrip, dripYears, reverseMode, isApplied, setYears, setGrowthRate, setPriceUpside, setReinvestDrip, setDripYears }) {
  const rows = [];
  // Baseline assumptions row — ALWAYS visible, non-removable. Each value
  // (5y, DRIP state, 6%/yr, +3%/yr) is click-to-edit via a portal'd
  // InlinePopover so users can tweak inline without scrolling back up
  // to the Time Machine card. Updates live.
  rows.push({ id: 'baseline', kind: 'baseline' });
  // DCA row comes next when enabled so it's always visible in the top-3.
  if (dca.enabled && dca.monthly > 0 && dca.tickers.length > 0) {
    const monthlyTotal = dca.split ? dca.monthly : dca.monthly * dca.tickers.length;
    rows.push({
      id: 'dca',
      kind: 'dca',
      tickers: dca.tickers,
      text: dca.split
        ? `DCA: ${fmtMoney(dca.monthly)}/mo split across ${dca.tickers.join(', ')} (${fmtMoney(monthlyTotal)}/mo)`
        : `DCA: ${fmtMoney(dca.monthly)}/mo of EACH (${dca.tickers.join(', ')}) = ${fmtMoney(monthlyTotal)}/mo total`,
    });
  }
  changes.forEach((c) => {
    const t = (c.ticker || '').toUpperCase();
    let text = '';
    if (c.action === 'add') text = `Add ${c.shares} sh of ${t}`;
    else if (c.action === 'remove') text = `Remove ${t}`;
    else if (c.action === 'modify') text = `Modify ${t} → ${c.shares} sh`;
    else if (c.action === 'replace') text = `Replace ${t} → ${(c.replaceWith || '').toUpperCase()}`;
    else if (c.action === 'crash') text = `Crash ${t} −${c.crashPct ?? 75}% (price + dividend)`;
    else if (c.action === 'cut_div') text = `Cut ${t} dividend −${c.cutPct ?? 50}%`;
    rows.push({ id: c.id, kind: c.action, ticker: t, ticker2: c.replaceWith, text });
  });

  if (rows.length === 1) {
    // Only the baseline row is present — no user changes yet. Don't early-
    // return; fall through to render the panel so the baseline is visible.
  }

  const visible = showAll ? rows : rows.slice(0, 4);
  const overflow = rows.length - 4;

  const tickerChip = (t) => {
    if (!t) return null;
    const sd = stockMap[t];
    const name = sd?.name || '';
    return (
      <InfoTip
        term={name ? `${t} · ${name}` : t}
        def={sd
          ? `${name || t}${sd.sector ? ` · ${sd.sector}` : ''}${sd.dividend_yield ? ` · ${sd.dividend_yield.toFixed(2)}% yield` : ''}${sd.price ? ` · $${sd.price.toFixed(2)}` : ''}`
          : `Live data still loading for ${t}.`
        }
        className="!no-underline !decoration-transparent"
        iconSize={9}
      >
        <span className="text-blue-600 dark:text-blue-400 font-bold font-mono">{t}</span>
      </InfoTip>
    );
  };

  const renderText = (r) => {
    // Inline tickers in blue by splitting on the ticker substrings.
    // Each ticker chip is wrapped in an InfoTip that shows the company's
    // full name + sector + live yield + price on hover / tap.
    const tks = [r.ticker, r.ticker2, ...(r.tickers || [])].filter(Boolean);
    if (tks.length === 0) return r.text;
    const parts = r.text.split(new RegExp(`\\b(${tks.join('|')})\\b`));
    return parts.map((p, i) => tks.includes(p)
      ? <span key={i}>{tickerChip(p)}</span>
      : <span key={i}>{p}</span>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="whatif-changes-applied">
      {(changes.length > 0 || (dca.enabled && dca.monthly > 0 && dca.tickers.length > 0)) && (
        <p className="text-[11px] text-violet-600 dark:text-violet-400 font-medium leading-relaxed mb-2" data-testid="whatif-changes-hint">
          Click <span className="font-bold">What If? 🔮</span> above or below to run these changes and see the simulated results.
        </p>
      )}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-1" data-testid="whatif-changes-applied-title">
          <Target className="w-4 h-4 text-violet-500" /> {isApplied ? 'Changes Applied' : 'Changes to be Applied'}
          <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal ml-1">({rows.length - 1})</span>
        </h3>
        {changes.length > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-[10px] text-rose-500 hover:text-rose-400 hover:underline"
            data-testid="whatif-changes-clear"
          >
            Clear all
          </button>
        )}
      </div>
      <ul className="space-y-1 text-xs">
        {visible.map((r) => (
          <li key={r.id} className="flex items-center gap-1.5" data-testid={`whatif-applied-row-${r.id}`}>
            {r.kind === 'baseline' && (reverseMode
              ? <History className="w-3 h-3 text-amber-500 shrink-0" />
              : <Sparkles className="w-3 h-3 text-violet-500 shrink-0" />)}
            {r.kind === 'add' && <Plus className="w-3 h-3 text-emerald-500 shrink-0" />}
            {r.kind === 'remove' && <Minus className="w-3 h-3 text-rose-500 shrink-0" />}
            {r.kind === 'modify' && <Wand2 className="w-3 h-3 text-amber-500 shrink-0" />}
            {r.kind === 'replace' && <Repeat className="w-3 h-3 text-violet-500 shrink-0" />}
            {r.kind === 'crash' && <AlertTriangle className="w-3 h-3 text-rose-500 shrink-0" />}
            {r.kind === 'cut_div' && <TrendingDown className="w-3 h-3 text-rose-500 shrink-0" />}
            {r.kind === 'dca' && <DollarSign className="w-3 h-3 text-emerald-500 shrink-0" />}
            <span className={`flex-1 leading-snug ${r.kind === 'baseline' ? 'text-muted-foreground' : ''}`}>
              {r.kind === 'baseline'
                ? (
                  <BaselineChips
                    years={years}
                    growthRate={growthRate}
                    priceUpside={priceUpside}
                    reinvestDrip={reinvestDrip}
                    dripYears={dripYears}
                    reverseMode={reverseMode}
                    setYears={setYears}
                    setGrowthRate={setGrowthRate}
                    setPriceUpside={setPriceUpside}
                    setReinvestDrip={setReinvestDrip}
                    setDripYears={setDripYears}
                  />
                )
                : renderText(r)}
            </span>
            {r.kind !== 'dca' && r.kind !== 'baseline' && (
              <button
                type="button"
                onClick={() => onRemove(r.id)}
                className="p-0.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-500"
                title="Remove this change"
                data-testid={`whatif-applied-remove-${r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="w-full mt-2 pt-2 border-t border-border/50 flex items-center justify-center gap-1 text-[11px] font-semibold text-violet-600 dark:text-violet-400 hover:underline"
          data-testid="whatif-changes-toggle-all"
        >
          {showAll ? (
            <>
              <ChevronUpIcon /> Show Less
            </>
          ) : (
            <>
              <ChevronDownIcon /> View More ({overflow} more)
            </>
          )}
        </button>
      )}
    </div>
  );
}

function ChevronDownIcon() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
  );
}
function ChevronUpIcon() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.94l-3.71 3.83a.75.75 0 11-1.08-1.04l4.25-4.4a.75.75 0 011.08 0l4.25 4.4a.75.75 0 01-.02 1.06z" clipRule="evenodd"/></svg>
  );
}



function DcaPresetCard({ tickers, stockMap, value, onChange }) {
  const toggle = (t) => {
    const has = value.tickers.includes(t);
    onChange({ ...value, tickers: has ? value.tickers.filter((x) => x !== t) : [...value.tickers, t] });
  };
  const monthlyTotal = value.split ? value.monthly : value.monthly * value.tickers.length;

  // Highest-yielder among selected (for split leftover display).
  const yielder = value.tickers.length > 1
    ? [...value.tickers].sort((a, b) => (stockMap[b]?.dividend_yield || 0) - (stockMap[a]?.dividend_yield || 0))[0]
    : null;

  // Per-ticker $ allocation the pill will display.
  const perTickerAllocation = (t) => {
    if (!value.monthly || !value.tickers.length) return null;
    if (!value.split) return value.monthly;
    const baseFloor = Math.floor((value.monthly / value.tickers.length) * 100) / 100;
    const leftover = Math.round((value.monthly - baseFloor * value.tickers.length) * 100) / 100;
    return t === yielder ? baseFloor + leftover : baseFloor;
  };

  return (
    <div
      className={`rounded-xl border bg-card p-4 space-y-3 transition-opacity ${value.enabled ? '' : 'opacity-60'}`}
      data-testid="whatif-dca-card"
    >
      <div className="flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-emerald-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider">Monthly Buy (DCA)</h2>
        {/* On/Off switch — OFF by default. When off, DCA is excluded from
            the simulation regardless of the tickers or amount entered. */}
        <label className="ml-auto inline-flex items-center gap-1 cursor-pointer" title={value.enabled ? 'Click to disable DCA' : 'Click to enable DCA'}>
          <span className={`text-[10px] font-bold uppercase ${value.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
            {value.enabled ? 'On' : 'Off'}
          </span>
          <button
            type="button"
            onClick={() => onChange({ ...value, enabled: !value.enabled })}
            className={`relative w-9 h-5 rounded-full transition-colors ${value.enabled ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-muted'}`}
            data-testid="whatif-dca-toggle"
            role="switch"
            aria-checked={value.enabled}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow ring-1 ring-slate-300 dark:ring-transparent transition-transform ${value.enabled ? 'translate-x-4' : 'translate-x-0'}`}
            />
          </button>
        </label>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed font-medium tracking-normal">
        Simulate dollar-cost-averaging — buy a fixed amount of one or more tickers every month for the
        time-machine horizon. Newly bought shares pay dividends starting the next pay date (DRIP-on
        compounds them; DRIP-off accumulates them as cash).
      </p>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">$ per month</label>
        <Input
          type="number" min="0" step="10"
          value={value.monthly || ''}
          onChange={(e) => onChange({ ...value, monthly: parseFloat(e.target.value) || 0 })}
          placeholder="200"
          disabled={!value.enabled}
          className="h-9 text-sm font-mono mt-1"
          data-testid="whatif-dca-monthly"
        />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Mode</label>
        <div className="flex gap-1 mt-1">
          <button
            type="button"
            onClick={() => onChange({ ...value, split: false })}
            disabled={!value.enabled}
            className={`flex-1 px-2 py-1 rounded text-xs font-semibold disabled:opacity-60 ${
              !value.split ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60' : 'text-muted-foreground hover:bg-accent'
            }`}
            data-testid="whatif-dca-each"
            title="Buy $X worth of EACH selected ticker per month"
          >
            Each
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...value, split: true })}
            disabled={!value.enabled}
            className={`flex-1 px-2 py-1 rounded text-xs font-semibold disabled:opacity-60 ${
              value.split ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300/60' : 'text-muted-foreground hover:bg-accent'
            }`}
            data-testid="whatif-dca-split"
            title="Split $X across selected tickers per month — leftover cents go to highest yielder"
          >
            Split
          </button>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Buy these tickers</label>
        <div className="grid grid-cols-2 gap-1 mt-1 max-h-32 overflow-y-auto">
          {tickers.length === 0 && (
            <span className="col-span-2 text-[11px] text-muted-foreground italic">Add positions to your portfolio first.</span>
          )}
          {tickers.map((t) => {
            const checked = value.tickers.includes(t);
            const alloc = perTickerAllocation(t);
            const sd = stockMap[t];
            const name = sd?.name || '';
            const tipDef = sd
              ? `${name || t}${sd.sector ? ` · ${sd.sector}` : ''}${sd.dividend_yield ? ` · ${sd.dividend_yield.toFixed(2)}% yield` : ''}${sd.price ? ` · $${sd.price.toFixed(2)}` : ''}`
              : `Live data still loading for ${t}.`;
            return (
              <InfoTip
                key={t}
                term={name ? `${t} · ${name}` : t}
                def={tipDef}
                showIcon={false}
                underline={false}
                className="!no-underline !decoration-transparent w-full"
              >
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  disabled={!value.enabled}
                  className={`w-full flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono font-bold transition-colors disabled:opacity-60 ${
                    checked
                      ? 'bg-emerald-500 text-white'
                      : 'bg-muted/50 hover:bg-emerald-100 dark:hover:bg-emerald-950/40 text-foreground'
                  }`}
                  data-testid={`whatif-dca-ticker-${t}`}
                >
                  {checked && <Check className="w-3 h-3 shrink-0" />}
                  <span className="truncate">{t}</span>
                  {checked && alloc != null && (
                    <span className="ml-auto pl-1 border-l border-white/30 font-semibold">${alloc.toFixed(alloc < 10 ? 2 : 0)}</span>
                  )}
                </button>
              </InfoTip>
            );
          })}
        </div>
        {value.tickers.length > 0 && value.monthly > 0 && (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1.5 font-semibold leading-relaxed" data-testid="whatif-dca-summary">
            ≈ <span className="font-mono">{fmtMoney(monthlyTotal)}</span>/mo total · <span className="font-mono">{fmtMoney(monthlyTotal * 12)}</span>/yr · <span className="font-mono">{fmtMoney(monthlyTotal * 12, 0)}</span> × time machine years
          </p>
        )}
      </div>
    </div>
  );
}

// ----- Results Panel ---------------------------------------------------------
function BacktestResultsPanel({ result, stockMap }) {
  const {
    series, initialShares, dcaConfig, reinvestDrip, maxYears, drip, dcaUsed, benchmarkTickers, dripYears: snapshotDripYears,
  } = result;
  const benchTks = benchmarkTickers || [];

  // Local view-years state — drives the 1y-5y slider. Default = maxYears
  // (i.e. the window we actually fetched, typically 5). Slide left to
  // see "what if I'd started 1y ago instead?" against the same data.
  const [viewYears, setViewYears] = useState(maxYears || 5);

  // Toggle benchmark overlay on/off — sometimes users want a clean
  // portfolio-only view.
  const [showBench, setShowBench] = useState(true);

  // Reset to full window whenever a brand-new backtest lands.
  useEffect(() => { setViewYears(maxYears || 5); }, [series, maxYears]);

  // Recompute everything from the raw series on every slider move.
  // Cheap: <= 30 tickers × <= 60 months.
  const computed = useMemo(
    () => computeBacktestFromSeries(series, initialShares, dcaConfig, reinvestDrip, viewYears, benchTks, snapshotDripYears),
    [series, initialShares, dcaConfig, reinvestDrip, viewYears, benchTks, snapshotDripYears],
  );

  if (!computed) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Not enough historical data to backtest.
      </div>
    );
  }

  const {
    startDate, endDate, startValue, endValue, totalDividends,
    totalContrib, startShares, endShares, points, monthsReplayed,
    benchmarkEndValues, dripCutoffDate, dripOpportunity,
  } = computed;
  const netChange = endValue - startValue - totalContrib;
  const netPct = startValue > 0 ? (netChange / startValue) * 100 : 0;
  const totalReturn = startValue > 0 ? ((endValue - startValue - totalContrib + totalDividends) / startValue) * 100 : 0;
  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return d; }
  };
  const shareGrowth = [];
  // For each user-held ticker, estimate the dividend yield at the
  // backtest START by summing the next 12 months of real dividends paid
  // on this ticker from the `series` data and dividing by the close on
  // the first month. This is forward-looking at start (not trailing)
  // but for stable dividend payers the delta is typically < 0.5pp —
  // and it's grounded in actual Yahoo history rather than just a
  // current snapshot.
  const yieldThenForTicker = (tkr) => {
    const arr = (series && series[tkr]) || [];
    if (!arr.length) return null;
    // Find index of startDate in the array.
    const startIdx = arr.findIndex((r) => r.date === startDate);
    if (startIdx < 0) return null;
    const startClose = arr[startIdx]?.close || 0;
    if (startClose <= 0) return null;
    let div12 = 0;
    for (let i = startIdx; i < Math.min(arr.length, startIdx + 12); i += 1) {
      div12 += arr[i]?.div || 0;
    }
    if (div12 <= 0) return null;
    return (div12 / startClose) * 100;
  };
  Object.keys(endShares).forEach((tkr) => {
    const s0 = startShares[tkr] || 0;
    const s1 = endShares[tkr] || 0;
    const g = s0 > 0 ? ((s1 - s0) / s0) * 100 : 0;
    shareGrowth.push({ ticker: tkr, s0, s1, g, yieldThen: yieldThenForTicker(tkr) });
  });
  shareGrowth.sort((a, b) => b.g - a.g);

  // Build the suffix for the share-growth explainer line, which needs
  // to reflect the current view-years AND whatever compounding
  // mechanism is actually active for this run.
  const yearLabel = viewYears === 1 ? '1y' : `${viewYears}y`;
  const mechLabel = (() => {
    if (drip && dcaUsed) return 'DRIP + DCA';
    if (drip) return 'DRIP';
    if (dcaUsed) return 'DCA';
    return 'buy-and-hold';
  })();

  return (
    <div className="space-y-4" data-testid="whatif-backtest-panel">
      {/* Callout */}
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-center gap-2">
        <History className="w-5 h-5 text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900 dark:text-amber-200" data-testid="whatif-backtest-callout-title">
            Historical Backtest · {fmtDate(startDate)} → {fmtDate(endDate)}
          </div>
          <div className="text-[11px] text-amber-800/80 dark:text-amber-200/70 mt-0.5" data-testid="whatif-backtest-callout-subtitle">
            Real monthly prices + dividends · last {yearLabel} · {monthsReplayed} months
            {drip ? ' · DRIP ON' : ' · DRIP OFF'}
            {dcaUsed ? ' · DCA active' : ''}
          </div>
        </div>
      </div>

      {/* Punchy 3-way comparison callout — your portfolio vs putting the
          SAME starting $ into 100% SCHD or 100% VYM on day 1 with DRIP.
          Dollar amounts land harder than percentages and answer the
          question: did my stock-picking + DRIP + DCA beat just buying
          the benchmark? */}
      {benchTks.length > 0 && benchmarkEndValues && startValue > 0 && (
        <div
          className="rounded-xl border border-border bg-card p-3"
          data-testid="whatif-backtest-headline"
        >
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
            If you'd started {yearLabel} ago with <span className="font-mono">{fmtMoney(startValue, 0)}</span>…
          </div>
          <div className="grid grid-cols-3 gap-2">
            {/* Sort: render SCHD, VYM on either side of a highlighted
                "yours" card. Column order is SCHD · YOU · VYM so the
                portfolio sits visually centred between its two peers. */}
            {(() => {
              const cards = [];
              const schdEnd = benchmarkEndValues.SCHD;
              const vymEnd = benchmarkEndValues.VYM;
              // Beats SCHD? Beats VYM? Drives the split-border on the
              // centre card so the user can read at a glance:
              //   both green  = portfolio beats BOTH benchmarks
              //   both red    = portfolio trails BOTH benchmarks
              //   green + red = mixed result (beats one, trails the other)
              const beatsSCHD = benchTks.includes('SCHD') && schdEnd != null ? endValue >= schdEnd : null;
              const beatsVYM = benchTks.includes('VYM') && vymEnd != null ? endValue >= vymEnd : null;
              if (benchTks.includes('SCHD')) {
                const v = schdEnd || 0;
                const delta = endValue - v;
                const sparkValues = points.map((p) => p.bench_SCHD).filter((x) => x != null);
                cards.push({ key: 'SCHD', label: '100% SCHD', value: v, delta, tone: 'blue', sparkValues, sparkColor: BENCHMARK_COLORS[0] });
              }
              cards.push({
                key: 'you', label: 'Your Portfolio', value: endValue, delta: 0, tone: 'amber',
                highlight: true, beatsSCHD, beatsVYM,
                sparkValues: points.map((p) => p.value),
                sparkColor: '#f59e0b',
              });
              if (benchTks.includes('VYM')) {
                const v = vymEnd || 0;
                const delta = endValue - v;
                const sparkValues = points.map((p) => p.bench_VYM).filter((x) => x != null);
                cards.push({ key: 'VYM', label: '100% VYM', value: v, delta, tone: 'violet', sparkValues, sparkColor: BENCHMARK_COLORS[1] });
              }
              return cards.map((c) => {
                const beats = c.delta > 0;
                const toneBg = {
                  amber: 'bg-amber-50 dark:bg-amber-950/40',
                  blue: 'bg-blue-50/60 dark:bg-blue-950/30 border-blue-200/60 dark:border-blue-900/40',
                  violet: 'bg-violet-50/60 dark:bg-violet-950/30 border-violet-200/60 dark:border-violet-900/40',
                }[c.tone];
                const toneText = {
                  amber: 'text-amber-700 dark:text-amber-300',
                  blue: 'text-blue-700 dark:text-blue-300',
                  violet: 'text-violet-700 dark:text-violet-300',
                }[c.tone];
                // Split-border rendering for the "Your Portfolio" card:
                // left half = vs SCHD, right half = vs VYM. Green when
                // beating that benchmark, red when trailing. Implemented
                // via a two-child absolutely-positioned border overlay
                // so the two halves can have different colors at once.
                const leftColor = c.beatsSCHD == null
                  ? 'border-amber-400/60 dark:border-amber-600/60'
                  : c.beatsSCHD
                    ? 'border-emerald-500 dark:border-emerald-500'
                    : 'border-rose-500 dark:border-rose-500';
                const rightColor = c.beatsVYM == null
                  ? 'border-amber-400/60 dark:border-amber-600/60'
                  : c.beatsVYM
                    ? 'border-emerald-500 dark:border-emerald-500'
                    : 'border-rose-500 dark:border-rose-500';
                return (
                  <div
                    key={c.key}
                    className={`relative rounded-lg border p-2 ${toneBg} ${c.highlight ? 'border-transparent' : ''}`}
                    data-testid={`whatif-backtest-headline-${c.key}`}
                  >
                    {/* Split border overlay — only on the "Your Portfolio" card */}
                    {c.highlight && (
                      <>
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-l-lg border-2 border-r-0 ${leftColor}`}
                          data-testid="whatif-backtest-headline-you-left-border"
                        />
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-y-0 right-0 w-1/2 rounded-r-lg border-2 border-l-0 ${rightColor}`}
                          data-testid="whatif-backtest-headline-you-right-border"
                        />
                      </>
                    )}
                    <div className={`relative text-[10px] font-bold uppercase tracking-wider ${toneText}`}>{c.label}</div>
                    <div className="relative flex items-end justify-between gap-1 mt-0.5">
                      <div className="text-sm sm:text-base font-mono font-bold leading-none">{fmtMoney(c.value, 0)}</div>
                      {c.sparkValues && c.sparkValues.length >= 2 && (
                        <Sparkline
                          values={c.sparkValues}
                          color={c.sparkColor}
                          width={60}
                          height={18}
                        />
                      )}
                    </div>
                    {!c.highlight && (
                      <div
                        className={`relative text-[10px] font-mono font-semibold mt-0.5 ${beats ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
                      >
                        {beats ? 'You +' : 'You '}{fmtMoney(c.delta, 0)}
                      </div>
                    )}
                    {c.highlight && (
                      <div className="relative text-[10px] text-muted-foreground mt-0.5 font-medium">
                        {drip ? 'DRIP on · ' : 'DRIP off · '}{dcaUsed ? 'DCA active' : 'no DCA'}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          <div className="text-[10px] text-muted-foreground mt-2 italic">
            Benchmarks assume 100% of your starting ${fmtMoney(startValue, 0)} went into SCHD or VYM on day 1, {drip ? 'with DRIP on' : 'no DRIP'}{dcaUsed ? ', and NO DCA (for a clean same-$ comparison)' : ''}.
          </div>

          {/* "What it would take to match" goal-setting hint — only
              rendered when the portfolio is trailing at least one
              benchmark. Converts an abstract "-4.7pp" into concrete
              "need $X more today, or $Y/mo DCA over N years at SCHD's
              actual annualized pace over this window." */}
          {(() => {
            const trailing = [];
            // Compute annualized-return (CAGR) of each benchmark over
            // the current window from the same raw points we already
            // have. Years fraction = monthsReplayed / 12. CAGR formula:
            //   (end / start) ^ (12 / months) - 1
            const yearsFrac = Math.max(monthsReplayed / 12, 1 / 12);
            const cagr = (endV) => {
              if (startValue <= 0 || endV <= 0) return null;
              return Math.pow(endV / startValue, 1 / yearsFrac) - 1;
            };
            // PMT required to grow to `gap` over `catchupYears` at
            // annualized return `r`. If r ≈ 0 we fall back to the
            // simple divided-by-months estimate.
            const requiredPmt = (gap, annualRate, years) => {
              const months = years * 12;
              if (!annualRate || Math.abs(annualRate) < 1e-4) return gap / months;
              const r = annualRate / 12;
              return gap * r / (Math.pow(1 + r, months) - 1);
            };
            if (benchTks.includes('SCHD') && benchmarkEndValues.SCHD != null && endValue < benchmarkEndValues.SCHD) {
              trailing.push({
                tkr: 'SCHD',
                gap: benchmarkEndValues.SCHD - endValue,
                annualRate: cagr(benchmarkEndValues.SCHD),
              });
            }
            if (benchTks.includes('VYM') && benchmarkEndValues.VYM != null && endValue < benchmarkEndValues.VYM) {
              trailing.push({
                tkr: 'VYM',
                gap: benchmarkEndValues.VYM - endValue,
                annualRate: cagr(benchmarkEndValues.VYM),
              });
            }
            if (trailing.length === 0) return null;
            const catchupYears = Math.max(viewYears, 3);
            return (
              <div
                className="mt-3 pt-3 border-t border-border/40 space-y-1.5"
                data-testid="whatif-backtest-catchup"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                  <Target className="w-3 h-3" /> What it would take to match
                </div>
                {trailing.map((t) => {
                  const monthly = requiredPmt(t.gap, t.annualRate || 0, catchupYears);
                  const pacePct = t.annualRate != null ? (t.annualRate * 100).toFixed(1) : null;
                  return (
                    <div
                      key={t.tkr}
                      className="text-[11px] leading-relaxed"
                      data-testid={`whatif-backtest-catchup-${t.tkr}`}
                    >
                      <span className="font-mono font-bold text-rose-600 dark:text-rose-400">{t.tkr}:</span>{' '}
                      you'd need another{' '}
                      <span className="font-mono font-bold">{fmtMoney(t.gap, 0)}</span>{' '}
                      to match today — or roughly{' '}
                      <span className="font-mono font-bold">{fmtMoney(monthly, 0)}/mo</span>{' '}
                      of fresh DCA over the next {catchupYears} years
                      {pacePct != null
                        ? <> at {t.tkr}'s trailing {yearLabel} <span className="font-mono font-semibold">{pacePct}%/yr</span> pace.</>
                        : <> at a similar growth rate.</>
                      }
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* View-years slider — slices the already-fetched 5y window down
          to 1y-5y without a refetch. Snaps to whole years. */}
      <div className="rounded-xl border border-border bg-card p-3" data-testid="whatif-backtest-view-slider-wrap">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip
              term="Backtest Window"
              def="Slide from 1 year to 5 years to see how your current share count would have performed over different historical windows. All numbers, charts, and the share-growth table below recompute instantly — no extra API calls."
            >
              Backtest Window
            </InfoTip>
          </div>
          <div className="text-sm font-mono font-bold text-amber-600 dark:text-amber-400" data-testid="whatif-backtest-view-label">
            Last {yearLabel}
          </div>
        </div>
        <input
          type="range"
          min={1}
          max={maxYears || 5}
          step={1}
          value={viewYears}
          onChange={(e) => setViewYears(Number(e.target.value))}
          className="w-full accent-amber-500"
          data-testid="whatif-backtest-view-slider"
        />
        <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5 px-0.5">
          {Array.from({ length: maxYears || 5 }, (_, i) => i + 1).map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setViewYears(y)}
              className={`font-mono ${viewYears === y ? 'text-amber-600 dark:text-amber-400 font-bold' : 'hover:text-foreground'}`}
              data-testid={`whatif-backtest-view-tick-${y}y`}
            >
              {y}y
            </button>
          ))}
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="whatif-backtest-metrics">
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip
              term="Start Value"
              def="Value of your current share count priced at the FIRST month of the backtest window — i.e. what this portfolio would have been worth if you'd owned these shares N years ago."
            >
              Start Value
            </InfoTip>
          </div>
          <div className="text-lg font-mono font-bold" data-testid="whatif-backtest-start-value">{fmtMoney(startValue, 0)}</div>
          <div className="text-[10px] text-muted-foreground">{fmtDate(startDate)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip
              term="End Value"
              def="Portfolio value at the LAST month of the backtest window — uses real closing prices plus any new shares purchased via DRIP reinvestment or DCA buys during the replay."
            >
              End Value
            </InfoTip>
          </div>
          <div className="text-lg font-mono font-bold" data-testid="whatif-backtest-end-value">{fmtMoney(endValue, 0)}</div>
          <div className={`text-[10px] font-mono font-semibold ${netPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {netPct >= 0 ? '+' : ''}{netPct.toFixed(1)}% price
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip
              term="Divs Received"
              def="Sum of every dividend paid during the backtest window using REAL historical dividend events. With DRIP on, those dividends also turn into more shares at that month's real close price."
            >
              Divs Received
            </InfoTip>
          </div>
          <div className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400" data-testid="whatif-backtest-divs">
            {fmtMoney(totalDividends, 0)}
          </div>
          <div className="text-[10px] text-muted-foreground">{drip ? 'reinvested' : 'paid as cash'}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip
              term="Total Return"
              def="Total return % including price appreciation AND dividends received, net of any DCA contributions. Formula: (End Value − Start Value − DCA Added + Dividends Received) ÷ Start Value × 100."
            >
              Total Return
            </InfoTip>
          </div>
          <div className={`text-lg font-mono font-bold ${totalReturn >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} data-testid="whatif-backtest-total-return">
            {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
          </div>
          {totalContrib > 0 && (
            <div className="text-[10px] text-muted-foreground">DCA: {fmtMoney(totalContrib, 0)} added</div>
          )}
        </div>
      </div>

      {/* Value curve */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-bold">Portfolio Value — Real History ({yearLabel})</h3>
          <div className="flex items-center gap-3">
            {benchTks.length > 0 && (
              <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer" data-testid="whatif-backtest-bench-toggle-wrap">
                <input
                  type="checkbox"
                  checked={showBench}
                  onChange={(e) => setShowBench(e.target.checked)}
                  className="accent-blue-500"
                  data-testid="whatif-backtest-bench-toggle"
                />
                <span>vs {benchTks.join(' / ')}</span>
              </label>
            )}
            <span className="text-[10px] text-muted-foreground">monthly closes</span>
          </div>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={points} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); } catch { return d; } }}
                minTickGap={40}
              />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
              <ReTooltip
                formatter={(v) => fmtMoney(v, 0)}
                labelFormatter={(l) => fmtDate(l)}
                contentStyle={{ fontSize: 12 }}
              />
              <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2.5} dot={false} name="Your Portfolio" />
              <Line type="monotone" dataKey="cumDividends" stroke="#10b981" strokeWidth={1.5} dot={false} strokeDasharray="4 4" name="Cum. Divs" />
              {dripCutoffDate && (
                // Subtle rose-tinted vertical line at the month DRIP
                // was cut off, so the user can see the slope change
                // after that date is the DRIP stop-point rather than
                // the market doing something.
                <ReferenceLine
                  x={dripCutoffDate}
                  stroke="#f43f5e"
                  strokeDasharray="3 3"
                  strokeOpacity={0.7}
                  label={{
                    value: 'DRIP off →',
                    position: 'insideTopRight',
                    fill: '#f43f5e',
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                  data-testid="whatif-backtest-drip-cutoff-line"
                />
              )}
              {showBench && benchTks.map((btkr, i) => (
                <Line
                  key={btkr}
                  type="monotone"
                  dataKey={`bench_${btkr}`}
                  stroke={BENCHMARK_COLORS[i % BENCHMARK_COLORS.length]}
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="2 3"
                  name={btkr}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* DRIP opportunity cost pill — rendered only when partial DRIP
            was active. Sits just under the chart so it visually
            connects to the rose ReferenceLine above. */}
        {dripOpportunity && dripOpportunity.cashReceived > 0 && (
          <div
            className="flex flex-wrap items-start gap-2 mt-3 pt-3 border-t border-rose-200/40 dark:border-rose-900/40"
            data-testid="whatif-backtest-drip-opportunity"
          >
            <span className="text-[10px] uppercase tracking-wider font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap">
              DRIP Opportunity Cost
            </span>
            <InfoTip
              term="DRIP Opportunity Cost"
              def={`After you turned DRIP off on ${(() => { try { return new Date(dripCutoffDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return dripCutoffDate; } })()}, you received ${fmtMoney(dripOpportunity.cashReceived, 0)} of dividends as CASH instead of reinvesting them. Had you kept DRIP on, those dividends (compounded at your portfolio's own measured growth rate) would theoretically have grown into about ${fmtMoney(dripOpportunity.couldHaveGrownTo, 0)} by ${(() => { try { return new Date(endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return endDate; } })()}. The ~${fmtMoney(dripOpportunity.foregoneGrowth, 0)} gap is pure compounding you left on the table — but it's cash you actually had in hand, so the trade-off is liquidity vs long-term growth.`}
              iconSize={9}
              showIcon={false}
              className="!no-underline"
            >
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300"
                data-testid="whatif-backtest-drip-opportunity-pill"
              >
                ~{fmtMoney(dripOpportunity.cashReceived, 0)} as cash
                {dripOpportunity.foregoneGrowth > 0 && (
                  <>
                    {' · could have grown to ~'}{fmtMoney(dripOpportunity.couldHaveGrownTo, 0)}
                  </>
                )}
              </span>
            </InfoTip>
          </div>
        )}
        {/* Benchmark delta summary — tiny row of "You vs SCHD: +X%" pills */}
        {showBench && benchTks.length > 0 && benchmarkEndValues && startValue > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/40" data-testid="whatif-backtest-bench-summary">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">vs Benchmark</span>
            {benchTks.map((btkr) => {
              const benchEnd = benchmarkEndValues[btkr] || 0;
              const portfolioReturnPct = startValue > 0 ? ((endValue - startValue - totalContrib + totalDividends) / startValue) * 100 : 0;
              const benchReturnPct = startValue > 0 ? ((benchEnd - startValue) / startValue) * 100 : 0;
              const delta = portfolioReturnPct - benchReturnPct;
              const beats = delta >= 0;
              // Plain-English verdict so the user doesn't have to eyeball
              // the magnitude of the gap. Cutoffs chosen so a 0.5pp result
              // isn't oversold as "really good".
              const absDelta = Math.abs(delta);
              const verdict = (() => {
                if (absDelta < 1) return beats
                  ? 'Basically tied — within a percentage point, so you\'re matching the benchmark.'
                  : 'Basically tied — within a percentage point, so you\'re matching the benchmark.';
                if (absDelta < 3) return beats
                  ? 'Slightly better than the benchmark.'
                  : 'Slightly worse than the benchmark.';
                if (absDelta < 7) return beats
                  ? 'This is solid — meaningfully better than just buying the benchmark.'
                  : 'Underperforming — worth reviewing which holdings are dragging.';
                if (absDelta < 15) return beats
                  ? 'This is really good — clearly beating the benchmark over this window.'
                  : 'This is a big miss — the benchmark would have done much better.';
                return beats
                  ? 'This is excellent — crushing the benchmark by a wide margin.'
                  : 'This is a painful gap — the benchmark left your portfolio far behind.';
              })();
              const moreOrLess = beats ? 'more' : 'less';
              return (
                <InfoTip
                  key={btkr}
                  term={`You vs ${btkr} (${yearLabel})`}
                  def={`Over the last ${yearLabel}, your portfolio's total return is ${portfolioReturnPct.toFixed(1)}% while ${btkr} (same starting $, matching DRIP) returned ${benchReturnPct.toFixed(1)}%. That's ${absDelta.toFixed(1)} percentage points ${moreOrLess} — ${verdict}`}
                  className="!no-underline"
                  iconSize={9}
                  showIcon={false}
                >
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold border ${beats ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'}`}
                    data-testid={`whatif-backtest-bench-pill-${btkr}`}
                  >
                    {btkr}: {beats ? '+' : ''}{delta.toFixed(1)}pp
                  </span>
                </InfoTip>
              );
            })}
          </div>
        )}
      </div>

      {/* Share-growth table (if DRIP or DCA) */}
      {(drip || dcaUsed) && shareGrowth.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="whatif-backtest-share-growth">
          <h3 className="text-sm font-bold mb-2">Share Growth by Ticker</h3>
          <div className="text-[11px] text-muted-foreground mb-2" data-testid="whatif-backtest-share-growth-subtitle">
            Start shares → end shares after {yearLabel} of {mechLabel}.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-1.5 pr-2 text-left">Ticker</th>
                  <th className="py-1.5 px-2 text-right">Start sh</th>
                  <th className="py-1.5 px-2 text-right">End sh</th>
                  <th className="py-1.5 pl-2 text-right">Growth</th>
                </tr>
              </thead>
              <tbody>
                {shareGrowth.map((r) => {
                  const sd = (stockMap || {})[r.ticker] || {};
                  const name = sd.name || '';
                  const yieldToday = sd.dividend_yield ? `${sd.dividend_yield.toFixed(2)}% yield today` : '';
                  const sector = sd.sector ? ` · ${sd.sector}` : '';
                  // Historical yield estimate computed above via
                  // forward-12-month real dividends from the backtest
                  // series. Only include it in the tooltip when we
                  // have a reliable estimate AND it differs from
                  // today's by at least 0.1pp (otherwise it adds
                  // noise without insight).
                  const yieldThenStr = r.yieldThen != null
                    ? `${r.yieldThen.toFixed(2)}% yield at start of window (est)`
                    : '';
                  const yieldStrCombined = yieldThenStr && yieldToday
                    ? `${yieldThenStr} → ${yieldToday}`
                    : (yieldThenStr || yieldToday);
                  const defText = name
                    ? `${name}${sector}${yieldStrCombined ? ` · ${yieldStrCombined}` : ''}`
                    : `Live data still loading for ${r.ticker}.`;
                  return (
                    <tr key={r.ticker} className="border-b border-border/40">
                      <td className="py-1.5 pr-2 font-mono font-semibold">
                        <InfoTip
                          term={name ? `${r.ticker} · ${name}` : r.ticker}
                          def={defText}
                          className="!no-underline !decoration-transparent"
                          iconSize={9}
                        >
                          <span className="text-blue-600 dark:text-blue-400">{r.ticker}</span>
                        </InfoTip>
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{r.s0.toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{r.s1.toFixed(2)}</td>
                      <td className={`py-1.5 pl-2 text-right font-mono font-semibold ${r.g >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {r.g >= 0 ? '+' : ''}{r.g.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SimResultsPanel({ result, sectorPalette, stockMap }) {
  const { nowMetrics: now, whatIfMetrics: what, summary, params, projectionPoints } = result;
  const incomeDelta = what.income - now.income;
  const valueDelta = what.value - now.value;
  const yieldDelta = what.yieldPct - now.yieldPct;
  const riskDelta = (what.avgRisk || 0) - (now.avgRisk || 0);

  const isBoost = incomeDelta > 0;
  const tinyChange = Math.abs(incomeDelta) / Math.max(now.income, 1) < 0.01 && Math.abs(valueDelta) / Math.max(now.value, 1) < 0.01;
  const callout = (() => {
    if (tinyChange) return { tone: 'small', text: '🤏 Little changed — toggle DRIP, add a Quick Scenario, or tweak the time-machine sliders to see a difference.' };
    if (Math.abs(incomeDelta) / Math.max(now.income, 1) > 0.2) {
      return isBoost
        ? { tone: 'boost', text: `🚀 Income boost — your annual dividends would jump ${Math.abs((incomeDelta / Math.max(now.income, 1)) * 100).toFixed(1)}%!` }
        : { tone: 'drag', text: `🐢 Income drag — your annual dividends would drop ${Math.abs((incomeDelta / Math.max(now.income, 1)) * 100).toFixed(1)}%.` };
    }
    return isBoost
      ? { tone: 'small-boost', text: `✨ Slight income lift of ${fmtMoney(incomeDelta)}/yr.` }
      : { tone: 'small-drag', text: `⚠️ Slight income dip of ${fmtMoney(incomeDelta)}/yr.` };
  })();

  return (
    <div className="space-y-4" data-testid="whatif-results">
      {callout && (
        <div
          className={`rounded-lg px-4 py-2.5 text-sm font-semibold ${
            callout.tone === 'boost'
              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-200 border border-emerald-300/60'
              : callout.tone === 'drag'
              ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-200 border border-rose-300/60'
              : 'bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 border border-amber-300/40'
          }`}
          data-testid="whatif-callout"
        >
          {callout.text}
        </div>
      )}

      {/* Side-by-side scenario cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ScenarioCard
          label="Now"
          subtitle={`After ${params.years}y · DRIP: Off (always)`}
          tone="muted"
          metrics={now}
          testId="card-now"
        />
        <ScenarioCard
          label={tinyChange ? 'What If? · Little Changed' : 'What If'}
          subtitle={(() => {
            const dripDesc = !params.drip
              ? 'Off'
              : params.dripYears < params.years
                ? `On for first ${params.dripYears}y, then cash`
                : 'On';
            return `After ${params.years}y · DRIP: ${dripDesc} · Growth ${params.growthRate}%/yr · Price ${params.priceUpside >= 0 ? '+' : ''}${params.priceUpside}%/yr`;
          })()}
          tone="violet"
          metrics={what}
          deltas={{ income: incomeDelta, value: valueDelta, yieldPct: yieldDelta, risk: riskDelta }}
          testId="card-whatif"
        />
      </div>

      {/* Income projection chart */}
      <div className="rounded-xl border border-border bg-card p-4" data-testid="whatif-projection">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h3 className="text-sm font-bold uppercase tracking-wider">{params.years}-Year Income Projection</h3>
          <span className="text-[10px] text-muted-foreground">
            Div Growth {params.growthRate}%/yr · Price {params.priceUpside >= 0 ? '+' : ''}{params.priceUpside}%/yr · DRIP {params.drip ? 'on (right)' : 'off'}
          </span>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={projectionPoints} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="year" stroke="currentColor" fontSize={11} tickFormatter={(y) => `+${y}y`} />
              <YAxis stroke="currentColor" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <ReTooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v) => fmtMoney(v)}
                labelFormatter={(y) => `Year +${y}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Now" stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="What If" stroke="#a855f7" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sector pies */}
      {(now.sectorRows.length > 0 || what.sectorRows.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="whatif-sectors">
          <SectorPie title="Now" rows={now.sectorRows} palette={sectorPalette} testId="sector-pie-now" positions={now.finalPositions} />
          <SectorPie title="What If" rows={what.sectorRows} palette={sectorPalette} testId="sector-pie-whatif" positions={what.finalPositions} />
        </div>
      )}

      <HoldingsCompareTable result={result} stockMap={stockMap} />
    </div>
  );
}

import { WHATIF_GLOSSARY, getDef } from '@/lib/whatifGlossary';
import InfoTip from '@/components/InfoTip';
import InlinePopover from '@/components/InlinePopover';
import QualityBadge from '@/components/QualityBadge';

const tip = (key) => {
  const d = getDef(key);
  return d ? `${d.term} — ${d.def}` : '';
};

function ScenarioCard({ label, subtitle, tone, metrics, deltas, testId }) {
  const toneCls =
    tone === 'violet'
      ? 'border-violet-300/70 dark:border-violet-700/60 bg-violet-50 dark:bg-violet-950/30'
      : 'border-border bg-card';
  const riskBadge = metrics.avgRisk
    ? riskColorClasses(metrics.avgRisk <= 3 ? 'safe' : metrics.avgRisk <= 6 ? 'moderate' : 'risky')
    : null;
  const fmtDelta = (d, formatter) => {
    if (d == null || d === 0) return null;
    const sign = d > 0 ? '+' : '';
    const cls = d > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
    return <span className={`text-[10px] font-mono font-semibold ${cls}`}>{sign}{formatter(d)}</span>;
  };

  return (
    <div className={`rounded-xl border-2 p-4 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-bold uppercase tracking-widest">{label}</h3>
        {tone === 'violet' && <Sparkles className="w-4 h-4 text-violet-500" />}
      </div>
      <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed font-medium">{subtitle}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="total_value" testId="tip-total-value">Total Value</InfoTip>
          </p>
          <p className="text-base sm:text-lg font-mono font-bold">{fmtMoney(metrics.value, 0)}</p>
          {deltas && fmtDelta(deltas.value, (n) => fmtMoney(n, 0))}
          <p className="text-[9px] text-muted-foreground font-mono">
            <InfoTip k="stocks_value">Stocks</InfoTip> {fmtMoney(metrics.stocksValue, 0)} · <InfoTip k="cash">Cash</InfoTip> {fmtMoney(metrics.cash, 0)}
          </p>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="annual_income" testId="tip-annual-income">Annual Income</InfoTip>
          </p>
          <p className="text-base sm:text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmtMoney(metrics.income, 0)}</p>
          {deltas && fmtDelta(deltas.income, (n) => fmtMoney(n, 0))}
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="yield" testId="tip-yield">Yield</InfoTip>
          </p>
          <p className="text-base sm:text-lg font-mono font-bold">{fmtPct(metrics.yieldPct, 2)}</p>
          {deltas && fmtDelta(deltas.yieldPct, (n) => `${n.toFixed(2)}pp`)}
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="avg_risk" testId="tip-avg-risk">Avg Risk</InfoTip>
          </p>
          <div className="flex items-center gap-1.5">
            {riskBadge && <span className={`w-2 h-2 rounded-full ${riskBadge.dot}`} />}
            <p className="text-base sm:text-lg font-mono font-bold">
              {metrics.avgRisk ? `${metrics.avgRisk.toFixed(1)}/10` : '—'}
            </p>
          </div>
          {metrics.avgRisk && (
            <p className="text-[9px] text-muted-foreground">
              {riskLabel(metrics.avgRisk <= 3 ? 'safe' : metrics.avgRisk <= 6 ? 'moderate' : 'risky')}
            </p>
          )}
          {deltas && fmtDelta(-deltas.risk, (n) => `${(-n).toFixed(1)}`)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border/40">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="monthly_run_rate" testId="tip-monthly-run-rate">Monthly Run-Rate</InfoTip>
          </p>
          <p className="text-sm font-mono font-bold">{fmtMoney(metrics.monthly, 0)}</p>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <InfoTip k="positions" testId="tip-positions">Positions</InfoTip>
          </p>
          <p className="text-sm font-mono font-bold">{metrics.positionCount}</p>
        </div>
      </div>
    </div>
  );
}

function SectorPie({ title, rows, palette, testId, positions = [] }) {
  // Open-expansion state — clicking a sector row drills in to show the
  // individual tickers that make up that sector's slice, listed by value.
  const [expanded, setExpanded] = useState(null);
  if (rows.length === 0) return null;

  // Group positions by sector. Both sides use the SAME fallback logic
  // (`h.sd.sector || 'Other'`) so 'Other' lookups pick up every position
  // whose sector is blank / missing. Lookup is case-insensitive /
  // whitespace-tolerant as a safety net in case a fetch returns slightly
  // different casing between runs.
  const normaliseSector = (s) => (s || 'Other').toString().trim().toLowerCase();
  const bySector = {};
  positions.forEach((p) => {
    const raw = p.sector || 'Other';
    const key = normaliseSector(raw);
    if (!bySector[key]) bySector[key] = { displayName: raw.trim() || 'Other', items: [] };
    bySector[key].items.push(p);
  });
  Object.values(bySector).forEach((b) => b.items.sort((a, b2) => b2.value - a.value));

  const expandedKey = expanded ? normaliseSector(expanded) : null;
  const expandedRow = rows.find((r) => normaliseSector(r.sector) === expandedKey);
  const expandedBucket = expandedKey ? bySector[expandedKey] : null;

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <h3 className="text-xs font-bold uppercase tracking-wider mb-1">
        <InfoTip k="sector">{title} · Sector Mix</InfoTip>
        <span className="ml-1 text-[9px] font-medium text-muted-foreground normal-case tracking-normal">
          (click a sector to drill in)
        </span>
      </h3>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="sector"
              cx="50%"
              cy="50%"
              outerRadius={60}
              innerRadius={32}
              onClick={(_d, i) => {
                const sec = rows[i]?.sector;
                if (sec) setExpanded((prev) => (prev === sec ? null : sec));
              }}
            >
              {rows.map((r) => (
                <Cell
                  key={r.sector}
                  fill={palette[r.sector] || '#94a3b8'}
                  style={{ cursor: 'pointer', opacity: expanded && expanded !== r.sector ? 0.45 : 1 }}
                />
              ))}
            </Pie>
            <ReTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v, _n, p) => [fmtMoney(v, 0), p.payload.sector]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="text-[10px] grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
        {rows.slice(0, 8).map((r) => {
          const def = WHATIF_GLOSSARY[`sector_${r.sector}`];
          const isOpen = expanded === r.sector;
          return (
            <li key={r.sector} className="flex items-center gap-1 min-w-0">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: palette[r.sector] || '#94a3b8' }} />
              <InfoTip
                k={`sector_${r.sector}`}
                term={def ? undefined : r.sector}
                def={def ? undefined : `${r.pct.toFixed(1)}% of portfolio value`}
                className="!no-underline !decoration-transparent min-w-0 flex-1"
              >
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => (prev === r.sector ? null : r.sector))}
                  className={`w-full text-left truncate hover:underline ${isOpen ? 'font-bold text-violet-600 dark:text-violet-300' : ''}`}
                  data-testid={`whatif-sector-row-${r.sector}`}
                >
                  {r.sector}
                </button>
              </InfoTip>
              <span className="font-mono ml-auto text-muted-foreground shrink-0">{r.pct.toFixed(0)}%</span>
            </li>
          );
        })}
      </ul>
      {expanded && expandedBucket && expandedBucket.items.length > 0 && (
        <div
          className="mt-2 pt-2 border-t border-border/40 space-y-1"
          data-testid={`whatif-sector-drilldown-${expanded}`}
        >
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: palette[expanded] || '#94a3b8' }} />
              {expandedBucket.displayName} · {expandedBucket.items.length} holding{expandedBucket.items.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => setExpanded(null)}
              className="text-[10px] text-muted-foreground hover:text-foreground normal-case tracking-normal"
              data-testid={`whatif-sector-drilldown-close`}
            >
              close ✕
            </button>
          </div>
          <ul className="text-[11px] space-y-0.5">
            {expandedBucket.items.map((p) => {
              const sectorTotal = expandedRow?.value || 0;
              const sharePct = sectorTotal > 0 ? (p.value / sectorTotal) * 100 : 0;
              return (
                <li key={p.ticker} className="flex items-center gap-1 min-w-0">
                  <InfoTip
                    term={p.name ? `${p.ticker} · ${p.name}` : p.ticker}
                    def={`${p.name || p.ticker}${p.sector ? ` · ${p.sector}` : ''}${p.baseYield ? ` · ${p.baseYield.toFixed(2)}% current yield` : ''}${p.finalPrice ? ` · projected price ${fmtMoney(p.finalPrice)}` : ''}`}
                    showIcon={false}
                    underline={false}
                    className="!no-underline !decoration-transparent"
                  >
                    <span className="font-mono font-bold text-blue-600 dark:text-blue-400">{p.ticker}</span>
                  </InfoTip>
                  <span className="text-muted-foreground truncate">· {p.name || '—'}</span>
                  <span className="ml-auto font-mono shrink-0">{fmtMoney(p.value, 0)}</span>
                  <span className="font-mono text-muted-foreground shrink-0 w-10 text-right">
                    {sharePct.toFixed(0)}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {expanded && (!expandedBucket || expandedBucket.items.length === 0) && (
        <div
          className="mt-2 pt-2 border-t border-border/40"
          data-testid="whatif-sector-drilldown-empty"
        >
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>No per-ticker breakdown available for <span className="font-semibold text-foreground">{expanded}</span> yet — live data may still be populating.</span>
            <button
              type="button"
              onClick={() => setExpanded(null)}
              className="text-[10px] hover:text-foreground"
            >
              close ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HoldingsCompareTable({ result, stockMap }) {
  const sim = result.whatIfMetrics?.finalPositions || [];
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="whatif-holdings">
      <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
        <Target className="w-3 h-3 text-violet-500" /> Projected Holdings · Year {result.params.years} ({sim.length})
      </h3>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border/60">
              <th className="text-left px-2 py-1 font-semibold">Ticker</th>
              <th className="text-center px-2 py-1 font-semibold">Quality</th>
              <th className="text-right px-2 py-1 font-semibold">Shares</th>
              <th className="text-right px-2 py-1 font-semibold">Price</th>
              <th className="text-right px-2 py-1 font-semibold">
                <InfoTip k="total_value">Value</InfoTip>
              </th>
              <th className="text-right px-2 py-1 font-semibold">
                <InfoTip k="annual_income">Annual Div Income</InfoTip>
              </th>
              <th className="text-center px-2 py-1 font-semibold">
                <InfoTip k="avg_risk">Risk</InfoTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {sim
              .sort((a, b) => b.value - a.value)
              .map((row) => {
                const cls = row.risk ? riskColorClasses(row.risk.category) : null;
                const sd = (stockMap || {})[row.ticker];
                return (
                  <tr key={row.ticker} className="border-b border-border/30 odd:bg-muted/10" data-testid={`whatif-holdings-row-${row.ticker}`}>
                    <td className="px-2 py-1 font-mono font-bold text-blue-600 dark:text-blue-400">
                      <InfoTip
                        term={row.name ? `${row.ticker} · ${row.name}` : row.ticker}
                        def={row.name
                          ? `${row.name}${row.sector ? ` · ${row.sector}` : ''}${row.baseYield ? ` · ${row.baseYield.toFixed(2)}% current yield` : ''}`
                          : `Projected end-of-horizon state for ${row.ticker}.`
                        }
                        showIcon={false}
                        underline={false}
                        className="!no-underline !decoration-transparent"
                      >
                        {row.ticker}
                      </InfoTip>
                    </td>
                    <td className="px-2 py-1 text-center">
                      {sd ? <QualityBadge stock={sd} size="xs" testIdSuffix={`whatif-${row.ticker}`} /> : '—'}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{row.shares.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(row.finalPrice)}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(row.value, 0)}</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-600 dark:text-emerald-400">{fmtMoney(row.income, 0)}</td>
                    <td className="px-2 py-1 text-center">
                      {cls ? (
                        <span className="inline-flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${cls.dot}`} />
                          <span className="font-mono text-[10px]">{row.risk.score}</span>
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
